import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://bloxfruithub-backend.onrender.com/api/v1';

const api = async (path, options = {}) => {
  const token = localStorage.getItem('admin_token');
  const res = await fetch(`${API_BASE}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    cache: 'no-store',
    ...options,
  });
  if (res.status === 401) { localStorage.removeItem('admin_token'); window.location.reload(); }
  return res.json();
};

const fmt = (n) => Number(n || 0).toLocaleString('en-IN');

function formatShortOrderId(raw) {
  if (!raw || typeof raw !== 'string') return '';
  const m = raw.match(/^ORD-(\d+)-(\d+)$/);
  if (m) return m[1].slice(-6) + '-' + m[2];
  if (raw.startsWith('ORD-')) return raw.slice(4);
  return raw.length > 14 ? '…' + raw.slice(-10) : raw;
}
const today = () => new Date().toISOString().split('T')[0];

const buildDateParams = (filterType, customDate) => {
  const keywords = { today: 'today', yesterday: 'yesterday', thisweek: 'thisweek', thismonth: 'thismonth' };
  if (keywords[filterType]) return { date: keywords[filterType] };
  if (filterType === 'custom' && customDate) return { startDate: customDate, endDate: customDate };
  return {};
};

const StatusBadge = ({ status }) => {
  const map = {
    pending:    { cls: 'badge-amber',  dot: '#f5a623' },
    initiated:  { cls: 'badge-blue',   dot: '#3a8ef6' },
    processing: { cls: 'badge-purple', dot: '#9b6ff7' },
    successful: { cls: 'badge-green',  dot: '#34c89a' },
    completed:  { cls: 'badge-green',  dot: '#34c89a' },
    success:    { cls: 'badge-green',  dot: '#34c89a' },
    failed:     { cls: 'badge-red',    dot: '#e05c5c' },
    active:     { cls: 'badge-green',  dot: '#34c89a' },
    inactive:   { cls: 'badge-gray',   dot: '#aaa'    },
    blocked:    { cls: 'badge-red',    dot: '#e05c5c' },
    deleted:    { cls: 'badge-gray',   dot: '#aaa'    },
    verified:   { cls: 'badge-green',  dot: '#34c89a' },
    unverified: { cls: 'badge-amber',  dot: '#f5a623' },
    credit:     { cls: 'badge-green',  dot: '#34c89a' },
    debit:      { cls: 'badge-red',    dot: '#e05c5c' },
    cancelled:  { cls: 'badge-gray',   dot: '#aaa'    },
  };
  const s = status?.toLowerCase();
  const cfg = map[s] || { cls: 'badge-gray', dot: '#aaa' };
  return (
    <span className={`badge ${cfg.cls}`}>
      <span className="dot" style={{ background: cfg.dot }} />
      {status}
    </span>
  );
};

const StatCard = ({ label, value, sub, color }) => (
  <div className="stat-card">
    <div className="stat-label">{label}</div>
    <div className="stat-value" style={color ? { color } : {}}>{value ?? '—'}</div>
    {sub && <div className="stat-sub">{sub}</div>}
  </div>
);

const Spinner = () => <div className="spinner" />;

const Modal = ({ title, onClose, children }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal" onClick={e => e.stopPropagation()}>
      <div className="modal-header">
        <h2>{title}</h2>
        <button className="modal-close" onClick={onClose}>✕</button>
      </div>
      <div className="modal-body">{children}</div>
    </div>
  </div>
);

const DateFilterBar = ({ value, onChange, showAllTime = true }) => {
  const isCustom = value?.startsWith('custom:');
  const [start, end] = isCustom ? value.replace('custom:', '').split('|') : ['', ''];

  const handleRange = (type, val) => {
    const newStart = type === 'start' ? val : (start || '');
    const newEnd   = type === 'end'   ? val : (end   || '');
    if (newStart && newEnd) onChange(`custom:${newStart}|${newEnd}`);
    else if (newStart)      onChange(`custom:${newStart}|${newStart}`);
  };

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      {['today','yesterday','thisweek','thismonth'].map(k => (
        <button key={k} className={`tab-btn ${value === k ? 'tab-active' : ''}`}
          onClick={() => onChange(k)}>
          {{ today:'Today', yesterday:'Yesterday', thisweek:'This Week', thismonth:'This Month' }[k]}
        </button>
      ))}
      {showAllTime && (
        <button className={`tab-btn ${value === '' ? 'tab-active' : ''}`} onClick={() => onChange('')}>All Time</button>
      )}
      <div style={{ display:'flex', alignItems:'center', gap:4, background:'var(--bg-elevated)', border:`1px solid ${isCustom ? 'var(--yellow)' : 'var(--border)'}`, borderRadius:'var(--radius)', padding:'0 10px', height:34, transition:'border .15s' }}>
        <input type="date" className="filter-select"
          style={{ border:'none', background:'transparent', padding:'0', width:120, color: start ? 'var(--text-primary)' : 'var(--text-muted)', outline:'none' }}
          max={today()} value={start || ''} onChange={e => handleRange('start', e.target.value)} title="Start date" />
        <span style={{ color:'var(--text-muted)', fontSize:12, margin:'0 2px' }}>→</span>
        <input type="date" className="filter-select"
          style={{ border:'none', background:'transparent', padding:'0', width:120, color: end ? 'var(--text-primary)' : 'var(--text-muted)', outline:'none' }}
          min={start || ''} max={today()} value={end || ''} onChange={e => handleRange('end', e.target.value)} title="End date" />
        {isCustom && (
          <button onClick={() => onChange('')}
            style={{ background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:14, lineHeight:1, padding:'0 0 0 4px' }}
            title="Clear date range">✕</button>
        )}
      </div>
    </div>
  );
};

const parseDateFilter = (val) => {
  if (!val) return {};
  if (val.startsWith('custom:')) {
    const range = val.replace('custom:', '');
    const [start, end] = range.split('|');
    if (start && end && start !== end) return { date: `${start},${end}` };
    return { date: start || end };
  }
  return { date: val };
};

// ════════════════════════════════════════════════════════════════════════════════
// IMAGE PICKER
// ════════════════════════════════════════════════════════════════════════════════
const ImagePicker = ({ currentUrl, onFileSelect, onUrlChange, label = 'Image' }) => {
  const [mode, setMode] = useState(currentUrl ? 'url' : 'upload');
  const [preview, setPreview] = useState(currentUrl || '');
  const fileRef = useRef(null);

  useEffect(() => {
    setPreview(currentUrl || '');
    if (currentUrl) setMode('url');
  }, [currentUrl]);

  const handleFile = (file) => {
    if (!file) return;
    onFileSelect(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleUrl = (e) => {
    setPreview(e.target.value);
    onUrlChange(e.target.value);
  };

  return (
    <div>
      <span className="detail-label">{label}</span>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8, marginTop: 4 }}>
        <button type="button" onClick={() => { setMode('upload'); onFileSelect(null); }}
          style={{ padding: '4px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
            background: mode === 'upload' ? '#FADA1B' : 'var(--bg-elevated)',
            color: mode === 'upload' ? '#000' : 'var(--text-muted)',
            border: '1px solid var(--border)', fontWeight: 600 }}>
          📁 Upload File
        </button>
        <button type="button" onClick={() => { setMode('url'); onFileSelect(null); }}
          style={{ padding: '4px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
            background: mode === 'url' ? '#FADA1B' : 'var(--bg-elevated)',
            color: mode === 'url' ? '#000' : 'var(--text-muted)',
            border: '1px solid var(--border)', fontWeight: 600 }}>
          🔗 Paste URL
        </button>
      </div>

      {mode === 'upload' && (
        <div
          onClick={() => fileRef.current?.click()}
          style={{ border: '2px dashed var(--border)', borderRadius: 10, padding: '20px',
            textAlign: 'center', cursor: 'pointer', background: 'var(--bg-elevated)', transition: 'border .2s' }}
          onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#FADA1B'; }}
          onDragLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
          onDrop={e => {
            e.preventDefault();
            e.currentTarget.style.borderColor = 'var(--border)';
            handleFile(e.dataTransfer.files[0]);
          }}>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
            onChange={e => handleFile(e.target.files[0])} />
          {preview ? (
            <div>
              <img src={preview} alt="preview"
                style={{ width: 80, height: 80, borderRadius: 10, objectFit: 'cover', marginBottom: 8 }} />
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Click or drag to change image</div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 28, marginBottom: 6 }}>🖼️</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Click or drag & drop image here</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>PNG, JPG, WebP up to 5MB</div>
            </div>
          )}
        </div>
      )}

      {mode === 'url' && (
        <div>
          <input className="filter-select full-width" value={currentUrl || ''}
            onChange={handleUrl} placeholder="https://example.com/image.jpg" />
          {preview && (
            <img src={preview} alt="preview"
              style={{ marginTop: 8, width: 70, height: 70, borderRadius: 8, objectFit: 'cover' }} />
          )}
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// LOGIN
// ════════════════════════════════════════════════════════════════════════════════
const Login = ({ onLogin }) => {
  const [loginMode, setLoginMode] = useState('admin');
  const [step, setStep]       = useState('phone');
  const [phone, setPhone]     = useState('');
  const [otp, setOtp]         = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [sentTo, setSentTo]   = useState('');
  const [staffUsername, setStaffUsername] = useState('');
  const [staffPassword, setStaffPassword] = useState('');

  const sendOtp = async () => {
    if (!phone || phone.length < 10) { setError('Please enter a valid phone number'); return; }
    setLoading(true); setError('');
    try {
      const data = await api('/admin/send-otp', { method: 'POST', body: JSON.stringify({ phone }) });
      if (data.success || data.message?.toLowerCase().includes('sent') || data.data) {
        setSentTo(phone); setStep('otp');
      } else setError(data.message || 'Failed to send OTP.');
    } catch { setError('Connection failed.'); }
    setLoading(false);
  };

  const verifyOtp = async () => {
    if (!otp || otp.length < 4) { setError('Please enter the OTP'); return; }
    setLoading(true); setError('');
    try {
      const data = await api('/admin/verify-otp', { method: 'POST', body: JSON.stringify({ phone, otp }) });
      if (data.token || data.data?.token) {
        localStorage.setItem('admin_token', data.token || data.data.token);
        onLogin();
      } else setError(data.message || 'Invalid OTP.');
    } catch { setError('Connection failed.'); }
    setLoading(false);
  };

  const staffLogin = async () => {
    if (!staffUsername.trim()) { setError('Please enter your username'); return; }
    if (!staffPassword) { setError('Please enter your password'); return; }
    setLoading(true); setError('');
    try {
      const data = await api('/admin/staff/login', { method: 'POST', body: JSON.stringify({ username: staffUsername.trim(), password: staffPassword }) });
      if (data.success && data.token) {
        localStorage.setItem('admin_token', data.token);
        onLogin();
      } else {
        setError(data.message || 'Invalid credentials.');
      }
    } catch { setError('Connection failed.'); }
    setLoading(false);
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">⚡</div>
        <h1 className="login-title">BloxFruitHub</h1>
        <p className="login-sub">Admin Panel</p>
        <div style={{ display:'flex', gap:0, marginBottom:16, borderRadius:8, overflow:'hidden', border:'1px solid var(--border)' }}>
          <button onClick={() => { setLoginMode('admin'); setError(''); }}
            style={{ flex:1, padding:'8px 0', fontSize:13, fontWeight:600, cursor:'pointer', border:'none',
              background: loginMode === 'admin' ? '#FADA1B' : 'var(--bg-elevated)',
              color: loginMode === 'admin' ? '#000' : 'var(--text-muted)' }}>
            Admin Login
          </button>
          <button onClick={() => { setLoginMode('staff'); setError(''); }}
            style={{ flex:1, padding:'8px 0', fontSize:13, fontWeight:600, cursor:'pointer', border:'none',
              background: loginMode === 'staff' ? '#FADA1B' : 'var(--bg-elevated)',
              color: loginMode === 'staff' ? '#000' : 'var(--text-muted)' }}>
            Staff Login
          </button>
        </div>
        {error && <div className="error-box">{error}</div>}
        {loginMode === 'admin' ? (
          step === 'phone' ? (
            <>
              <div className="form-group">
                <label>Phone Number</label>
                <input type="tel" placeholder="Enter your phone number" value={phone}
                  onChange={e => setPhone(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendOtp()} />
              </div>
              <button className="btn btn-primary login-btn" onClick={sendOtp} disabled={loading}>
                {loading ? 'Sending OTP…' : 'Send OTP'}
              </button>
            </>
          ) : (
            <>
              <div className="otp-sent-msg">OTP sent to <strong>{sentTo}</strong></div>
              <div className="form-group">
                <label>Enter OTP</label>
                <input type="number" placeholder="Enter 6-digit OTP" value={otp}
                  onChange={e => setOtp(e.target.value)} onKeyDown={e => e.key === 'Enter' && verifyOtp()} autoFocus />
              </div>
              <button className="btn btn-primary login-btn" onClick={verifyOtp} disabled={loading}>
                {loading ? 'Verifying…' : 'Verify OTP & Login'}
              </button>
              <button className="btn login-btn-outline" onClick={() => { setStep('phone'); setError(''); setOtp(''); }}>
                ← Change phone number
              </button>
            </>
          )
        ) : (
          <>
            <div className="form-group">
              <label>Username</label>
              <input type="text" placeholder="Enter your username" value={staffUsername}
                onChange={e => setStaffUsername(e.target.value)} onKeyDown={e => e.key === 'Enter' && staffLogin()} />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input type="password" placeholder="Enter your password" value={staffPassword}
                onChange={e => setStaffPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && staffLogin()} />
            </div>
            <button className="btn btn-primary login-btn" onClick={staffLogin} disabled={loading}>
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// DASHBOARD
// ════════════════════════════════════════════════════════════════════════════════
const Dashboard = () => {
  const [data, setData]       = useState(null);
  const [otpBal, setOtpBal]   = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const [dash, otp] = await Promise.all([
        api('/admin/dashboard/stats'),
        api('/admin/api-balance'),
      ]);
      setData(dash?.data || dash);
      setOtpBal(otp?.data?.oneApiOtpBalance ?? otp?.data?.otpBalance ?? null);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  if (loading) return <div className="center"><Spinner /></div>;
  const d = data || {};

  const successOrders   = d.orders?.success?.count           ?? d.todaySuccessOrders ?? 0;
  const successOrderAmt = d.orders?.success?.amount          ?? d.todaySuccessOrderAmount ?? 0;
  const pendingOrders   = d.orders?.pendingProcessing?.count ?? d.todayPendingProcessingOrders ?? 0;
  const pendingOrderAmt = d.orders?.pendingProcessing?.amount ?? d.todayPendingProcessingOrderAmount ?? 0;
  const failedOrders    = d.orders?.failed?.count            ?? d.todayFailedOrders ?? 0;
  const failedOrderAmt  = d.orders?.failed?.amount           ?? d.todayFailedOrderAmount ?? 0;
  const successTxns     = d.transactions?.success?.count     ?? d.todaySuccessTransactions ?? 0;
  const successTxnAmt   = d.transactions?.success?.amount    ?? d.todaySuccessTransactionAmount ?? 0;
  const pendingTxns     = d.transactions?.pending?.count     ?? d.todayPendingTransactions ?? 0;
  const pendingTxnAmt   = d.transactions?.pending?.amount    ?? d.todayPendingTransactionAmount ?? 0;
  const failedTxns      = d.transactions?.failed?.count      ?? d.todayFailedTransactions ?? 0;
  const failedTxnAmt    = d.transactions?.failed?.amount     ?? d.todayFailedTransactionAmount ?? 0;
  const newUsers        = d.users?.newUsers ?? d.todayNewUsers ?? 0;
  const profit          = d.profit?.totalProfit ?? 0;
  const revenue         = d.profit?.totalRevenue ?? 0;

  return (
    <div className="section">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:18, color:'#3a8ef6' }}>🕐</span>
          <h2 style={{ fontSize:18, fontWeight:600, margin:0 }}>Today's Report</h2>
        </div>
        {otpBal !== null && (
          <div className="stat-card" style={{ margin:0, minWidth:160 }}>
            <div className="stat-label">OTP Balance</div>
            <div className="stat-value" style={{ color:'#9b6ff7' }}>₹{fmt(otpBal)}</div>
          </div>
        )}
        <button className="btn" onClick={fetchDashboard}>↻ Refresh</button>
      </div>

      <div className="dash-report-grid">
        <div className="dash-report-card dash-success">
          <div className="dash-report-header"><span className="dash-report-icon">✅</span><span className="dash-report-title">Success</span></div>
          <div className="dash-report-cols">
            <div><div className="dash-col-label">ORDERS</div><div className="dash-col-amount" style={{ color:'#1a8a55' }}>₹{fmt(successOrderAmt)}</div><div className="dash-col-count">{successOrders} items</div></div>
            <div><div className="dash-col-label">TRANSACTIONS</div><div className="dash-col-amount" style={{ color:'#1a8a55' }}>₹{fmt(successTxnAmt)}</div><div className="dash-col-count">{successTxns} items</div></div>
          </div>
        </div>
        <div className="dash-report-card dash-pending">
          <div className="dash-report-header"><span className="dash-report-icon">🕐</span><span className="dash-report-title">Pending</span></div>
          <div className="dash-report-cols">
            <div><div className="dash-col-label">ORDERS</div><div className="dash-col-amount" style={{ color:'#c87a00' }}>₹{fmt(pendingOrderAmt)}</div><div className="dash-col-count">{pendingOrders} items</div></div>
            <div><div className="dash-col-label">TRANSACTIONS</div><div className="dash-col-amount" style={{ color:'#c87a00' }}>₹{fmt(pendingTxnAmt)}</div><div className="dash-col-count">{pendingTxns} items</div></div>
          </div>
        </div>
        <div className="dash-report-card dash-failed">
          <div className="dash-report-header"><span className="dash-report-icon">❌</span><span className="dash-report-title">Failed</span></div>
          <div className="dash-report-cols">
            <div><div className="dash-col-label">ORDERS</div><div className="dash-col-amount" style={{ color:'#c84040' }}>₹{fmt(failedOrderAmt)}</div><div className="dash-col-count">{failedOrders} items</div></div>
            <div><div className="dash-col-label">TRANSACTIONS</div><div className="dash-col-amount" style={{ color:'#c84040' }}>₹{fmt(failedTxnAmt)}</div><div className="dash-col-count">{failedTxns} items</div></div>
          </div>
        </div>
      </div>

      <div className="stat-grid" style={{ marginTop:16 }}>
        <StatCard label="Today's Revenue" value={`₹${fmt(revenue)}`}  color="#34c89a" />
        <StatCard label="Today's Profit"  value={`₹${fmt(profit)}`}   color="#9b6ff7" />
        <StatCard label="New Users Today" value={newUsers}             color="#3a8ef6" />
        <StatCard label="Profit Margin"   value={`${d.profit?.averageProfitMargin ?? 0}%`} />
      </div>
    </div>
  );
};

const getPlayerId = (order) => {
  if (order.playerId) return order.playerId;
  try { return JSON.parse(order.description || '{}').playerId || '—'; }
  catch { return '—'; }
};
const getDescription = (order) => {
  try { return JSON.parse(order.description || '{}').text || order.description || '—'; }
  catch { return order.description || '—'; }
};

// ════════════════════════════════════════════════════════════════════════════════
// ORDERS
// ════════════════════════════════════════════════════════════════════════════════
const Orders = () => {
  const [orders, setOrders]         = useState([]);
  const [orderStats, setOrderStats] = useState({});
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(1);
  const [total, setTotal]           = useState(0);
  const [statusFilter, setStatus]   = useState('');
  const [search, setSearch]         = useState('');
  const [editOrder, setEditOrder]   = useState(null);
  const [logsOrder, setLogsOrder]   = useState(null);
  const [newStatus, setNewStatus]   = useState('');
  const [dateFilter, setDateFilter] = useState('today');
  const [saving, setSaving]         = useState(false);
  const [saveMsg, setSaveMsg]       = useState('');
  const [mm2Orders, setMm2Orders]   = useState([]);
  const [mm2Loading, setMm2Loading] = useState(false);
  const [activeOrderTab, setActiveOrderTab] = useState('all');
  const [mm2SaveMsg, setMm2SaveMsg] = useState('');
  const [markingId, setMarkingId]   = useState(null);
  const LIMIT = 10;

  const doFetch = useRef(null);
  doFetch.current = async (p) => {
    setLoading(true);
    const params = new URLSearchParams({ page: p, limit: LIMIT });
    if (statusFilter) params.append('status', statusFilter);
    if (search) params.append('search', search);
    const dp = parseDateFilter(dateFilter);
    Object.entries(dp).forEach(([k,v]) => params.append(k, v));
    const data = await api(`/admin/order?${params}`);
    const d = data?.data || data;
    setOrders(d?.orders || []);
    setTotal(d?.pagination?.totalOrders || 0);
    setOrderStats(d?.stats || {});
    setLoading(false);
  };

  const fetchOrders = useCallback((p) => doFetch.current(p ?? page), [page]);
  useEffect(() => { doFetch.current(page); }, [page]);
  useEffect(() => { setPage(1); doFetch.current(1); }, [dateFilter, statusFilter, search]);

  const fetchMm2Orders = useCallback(async () => {
    setMm2Loading(true);
    try {
      const data = await api('/admin/order?limit=100&page=1');
      const d = data?.data || data;
      const allOrders = d?.orders || [];
      console.log('Total orders fetched:', allOrders.length);
      console.log('Orders with deliveryStatus:', allOrders.filter(o => o.deliveryStatus !== null && o.deliveryStatus !== undefined));
      setMm2Orders(allOrders.filter(o => o.deliveryStatus !== null && o.deliveryStatus !== undefined && o.deliveryStatus !== 'delivered' && o.deliveryStatus !== 'expired'));
    } catch { setMm2Orders([]); }
    setMm2Loading(false);
  }, []);

  useEffect(() => { fetchMm2Orders(); }, [fetchMm2Orders]);

  const markDelivered = async (order) => {
    try {
      const res = await fetch(`${API_BASE}/admin/order/status/${order._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
        body: JSON.stringify({ status: 'completed', deliveryStatus: 'delivered' }),
      });
      const data = await res.json();
      if (res.ok) { setMm2SaveMsg('✅ Order marked as delivered!'); fetchMm2Orders(); }
      else setMm2SaveMsg('❌ ' + (data.message || 'Failed'));
    } catch { setMm2SaveMsg('❌ Network error'); }
  };

  const markCompleted = async (order) => {
    setMarkingId(order._id);
    try {
      const data = await api(`/admin/orders/${order._id}/mark-completed`, { method: 'PATCH' });
      if (data.success || data.data) fetchOrders();
    } catch {}
    setMarkingId(null);
  };

  const saveStatus = async () => {
    setSaving(true); setSaveMsg('');
    try {
      const data = await api(`/admin/order/status/${editOrder._id}`, {
        method: 'PATCH', body: JSON.stringify({ status: newStatus }),
      });
      if (data.success || data.data) {
        setSaveMsg('✅ Status updated!');
        fetchOrders();
        setTimeout(() => setEditOrder(null), 1200);
      } else setSaveMsg('❌ ' + (data.message || 'Update failed'));
    } catch { setSaveMsg('❌ Network error'); }
    setSaving(false);
  };

  const pages = Math.ceil(total / LIMIT);
  const s = orderStats;

  return (
    <div className="section">
      <div className="tab-row" style={{ marginBottom: 12 }}>
        <button className={`tab-btn ${activeOrderTab === 'all' ? 'tab-active' : ''}`}
          onClick={() => setActiveOrderTab('all')}>🛒 All Orders</button>
        <button className={`tab-btn ${activeOrderTab === 'mm2' ? 'tab-active' : ''}`}
          onClick={() => setActiveOrderTab('mm2')}>🎮 MM2 Queue ({mm2Orders.length})</button>
      </div>
      <div className="stat-grid">
        <StatCard label="Total"     value={`₹${fmt(s.totalAmount)}`}     sub={`${s.totalOrders ?? 0} orders`} />
        <StatCard label="Completed" value={`₹${fmt(s.completedAmount)}`} sub={`${s.completedOrders ?? 0}`} color="#34c89a" />
        <StatCard label="Pending"   value={`₹${fmt(s.pendingAmount)}`}   sub={`${s.pendingOrders ?? 0}`}   color="#f5a623" />
        <StatCard label="Failed"    value={`₹${fmt(s.failedAmount)}`}    sub={`${s.failedOrders ?? 0}`}    color="#e05c5c" />
        <StatCard label="Cancelled" value={`₹${fmt(s.cancelledAmount)}`} sub={`${s.cancelledOrders ?? 0}`} color="#aaa" />
      </div>
      <div className="card">
        <div className="filters-row">
          <input className="search-input" placeholder="Search orders, customers, player ID…"
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          <DateFilterBar value={dateFilter} onChange={v => { setDateFilter(v); setPage(1); }} />
          <select className="filter-select" value={statusFilter} onChange={e => { setStatus(e.target.value); setPage(1); }}>
            <option value="">All Status</option>
            <option value="initiated">Initiated</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button className="btn" onClick={fetchOrders}>↻ Refresh</button>
        </div>
        {loading ? <div className="center"><Spinner /></div> : (
          <>
            <div className="table-wrap">
              <table>
                <thead><tr><th>#</th><th>Customer</th><th>Player ID</th><th>Amount</th><th>Order ID</th><th>Game</th><th>Status</th><th>Payment</th><th>Date</th><th>Actions</th></tr></thead>
                <tbody>
                  {orders.length === 0
                    ? <tr><td colSpan={10} className="empty">No orders found</td></tr>
                    : orders.map((o, i) => (
                      <tr key={o._id}>
                        <td>{(page-1)*LIMIT+i+1}</td>
                        <td><span className="customer-name">{o.userId?.name || o.customerName}</span><br/><span className="muted">{o.userId?.phone || o.phone}</span></td>
                        <td><span className="player-id">{getPlayerId(o)}</span></td>
                        <td><span className="amount">₹{fmt(o.amount)}</span></td>
                        <td><span className="order-id" title={o.orderId}>{formatShortOrderId(o.orderId)}</span></td>
                        <td>{o.gameName}</td>
                        <td><StatusBadge status={o.status} /></td>
                        <td><span className="pill-outline">{o.paymentMethod}</span></td>
                        <td className="muted">{new Date(o.createdAt).toLocaleDateString('en-IN')}</td>
                        <td style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                          <button className="btn-edit" onClick={() => setLogsOrder(o)}>📋</button>
                          {o.status?.toLowerCase() !== 'initiated' && (
                            <button className="btn-edit" onClick={() => { setEditOrder(o); setNewStatus(o.status); setSaveMsg(''); }}>✏</button>
                          )}
                          {!['completed','failed','cancelled'].includes(o.status?.toLowerCase()) && (
                            <button className="btn btn-primary" style={{ fontSize:11, height:28, padding:'0 8px' }}
                              disabled={markingId === o._id}
                              onClick={() => markCompleted(o)}>
                              {markingId === o._id ? '…' : '✅ Mark Completed'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <div className="pagination">
              <button className="btn btn-sm" disabled={page===1} onClick={() => setPage(p=>p-1)}>← Prev</button>
              <span className="page-info">Page {page} of {pages||1} ({total} total)</span>
              <button className="btn btn-sm" disabled={page>=pages} onClick={() => setPage(p=>p+1)}>Next →</button>
            </div>
          </>
        )}
      </div>

      {activeOrderTab === 'mm2' && (
        <div className="card">
          <div className="filters-row">
            <div className="card-title" style={{ fontSize:15, fontWeight:600 }}>MM2 Orders Queue</div>
            <button className="btn" onClick={fetchMm2Orders}>↻ Refresh</button>
          </div>
          {mm2SaveMsg && <div className={`save-msg ${mm2SaveMsg.startsWith('✅') ? 'msg-success' : 'msg-error'}`} style={{ margin:'8px 16px' }}>{mm2SaveMsg}</div>}
          {mm2Loading ? <div className="center"><Spinner /></div> : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Customer</th>
                    <th>Roblox Username</th>
                    <th>Item</th>
                    <th>Amount</th>
                    <th>Delivery Status</th>
                    <th>Expires</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {mm2Orders.length === 0
                    ? <tr><td colSpan={8} className="empty">No MM2 orders found</td></tr>
                    : mm2Orders.map((o, i) => (
                      <tr key={o._id}>
                        <td>{i + 1}</td>
                        <td>
                          <span className="customer-name">{o.userId?.name || o.customerName}</span>
                          <br/>
                          <span className="muted">{o.userId?.phone}</span>
                        </td>
                        <td><span className="player-id">{o.robloxUsername || '—'}</span></td>
                        <td><span className="customer-name">{o.gameName}</span></td>
                        <td><span className="amount">₹{fmt(o.amount)}</span></td>
                        <td><StatusBadge status={o.deliveryStatus || 'pending'} /></td>
                        <td className="muted" style={{ fontSize:11 }}>
                          {o.expiresAt ? new Date(o.expiresAt).toLocaleTimeString('en-IN') : '—'}
                        </td>
                        <td>
                          {o.deliveryStatus !== 'delivered' && o.deliveryStatus !== 'expired' && (
                            <button className="btn btn-primary" style={{ fontSize:12, height:30, padding:'0 12px' }}
                              onClick={() => markDelivered(o)}>
                              ✅ Mark Delivered
                            </button>
                          )}
                          {o.serverLink && (
                            <a href={o.serverLink} target="_blank" rel="noreferrer"
                              className="btn" style={{ fontSize:12, height:30, padding:'0 12px', marginLeft:6 }}>
                              🎮 Join Server
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {logsOrder && (
        <Modal title={`Logs — ${formatShortOrderId(logsOrder.orderId)}`} onClose={() => setLogsOrder(null)}>
          <div className="order-detail">
            <div className="log-json">
              {[
                ['Order ID', formatShortOrderId(logsOrder.orderId)],['Game', logsOrder.gameName],
                ['Amount', `₹${fmt(logsOrder.amount)}`],['Status', null],
                ['Player ID', getPlayerId(logsOrder)],['Customer', logsOrder.userId?.name || logsOrder.customerName],
                ['Phone', logsOrder.userId?.phone || logsOrder.phone],
                ['Email', logsOrder.userId?.email || logsOrder.email],
                ['Payment', logsOrder.paymentMethod],
                ['Created', new Date(logsOrder.createdAt).toLocaleString('en-IN')],
              ].map(([k,v]) => (
                <div key={k} className="log-row">
                  <span className="log-key">{k}</span>
                  <span className="log-val">{k==='Status' ? <StatusBadge status={logsOrder.status} /> : v}</span>
                </div>
              ))}
            </div>
            {logsOrder.items?.length > 0 && (
              <div className="items-section" style={{ marginTop:12 }}>
                <div className="items-title">Items</div>
                {logsOrder.items.map((item,i) => (
                  <div key={i} className="item-row">
                    <span>{item.itemName}</span>
                    <span className="muted">×{item.quantity}</span>
                    <span className="amount">₹{item.price}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="modal-actions" style={{ marginTop:12 }}>
              <button className="btn" onClick={() => setLogsOrder(null)}>Close</button>
              {logsOrder.status?.toLowerCase() !== 'initiated' && (
                <button className="btn btn-primary" onClick={() => { setLogsOrder(null); setEditOrder(logsOrder); setNewStatus(logsOrder.status); setSaveMsg(''); }}>✏ Edit Status</button>
              )}
            </div>
          </div>
        </Modal>
      )}

      {editOrder && (
        <Modal title="Edit Order Status" onClose={() => setEditOrder(null)}>
          <div className="order-detail">
            <div className="detail-grid">
              <div><span className="detail-label">Customer</span><span>{editOrder.userId?.name || editOrder.customerName}</span></div>
              <div><span className="detail-label">Amount</span><span className="amount">₹{fmt(editOrder.amount)}</span></div>
              <div><span className="detail-label">Game</span><span>{editOrder.gameName}</span></div>
              <div><span className="detail-label">Player ID</span><span className="player-id">{getPlayerId(editOrder)}</span></div>
            </div>
            <div className="status-update" style={{ marginTop:12 }}>
              <div className="current-status-row">Current: <StatusBadge status={editOrder.status} /></div>
              <select className="filter-select full-width" style={{ marginTop:8 }} value={newStatus} onChange={e => setNewStatus(e.target.value)}>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">✅ Completed</option>
                <option value="failed">❌ Failed (auto-refund)</option>
                <option value="cancelled">Cancelled</option>
              </select>
              {saveMsg && <div className={`save-msg ${saveMsg.startsWith('✅') ? 'msg-success' : 'msg-error'}`}>{saveMsg}</div>}
              <div className="modal-actions" style={{ marginTop:12 }}>
                <button className="btn" onClick={() => setEditOrder(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={saveStatus} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// TRANSACTIONS
// ════════════════════════════════════════════════════════════════════════════════
const Transactions = () => {
  const [txns, setTxns]       = useState([]);
  const [loading, setLoad]    = useState(true);
  const [page, setPage]       = useState(1);
  const [total, setTotal]     = useState(0);
  const [stats, setStats]     = useState({});
  const [dateFilter, setDate] = useState('today');
  const [search, setSearch]   = useState('');
  const LIMIT = 10;

  const txnFetch = useRef(null);
  txnFetch.current = async (p) => {
    setLoad(true);
    const params = new URLSearchParams({ page: p, limit: LIMIT });
    if (search) params.append('search', search);
    const dp = parseDateFilter(dateFilter);
    Object.entries(dp).forEach(([k,v]) => params.append(k, v));
    const data = await api(`/admin/transaction?${params}`);
    const d = data?.data || data;
    setTxns(d?.transactions || []);
    setTotal(d?.pagination?.totalTransactions || 0);
    setStats({ totalAmount: d?.stats?.totalAmount??0, successful: d?.stats?.successTransactions??0, pending: d?.stats?.pendingTransactions??0, failed: d?.stats?.failedTransactions??0 });
    setLoad(false);
  };

  const fetchTxns = useCallback((p) => txnFetch.current(p ?? page), [page]);
  useEffect(() => { txnFetch.current(page); }, [page]);
  useEffect(() => { setPage(1); txnFetch.current(1); }, [dateFilter, search]);
  const pages = Math.ceil(total / LIMIT);

  return (
    <div className="section">
      <div className="stat-grid">
        <StatCard label="Total Amount" value={`₹${fmt(stats.totalAmount)}`} color="#34c89a" />
        <StatCard label="Successful"   value={stats.successful ?? '—'}      color="#34c89a" />
        <StatCard label="Pending"      value={stats.pending ?? '—'}         color="#f5a623" />
        <StatCard label="Failed"       value={stats.failed ?? '—'}          color="#e05c5c" />
      </div>
      <div className="card">
        <div className="filters-row">
          <input className="search-input" placeholder="Search by name, phone, txn ID…"
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          <DateFilterBar value={dateFilter} onChange={v => { setDate(v); setPage(1); }} />
          <button className="btn" onClick={fetchTxns}>↻ Refresh</button>
        </div>
        {loading ? <div className="center"><Spinner /></div> : (
          <>
            <div className="table-wrap">
              <table>
                <thead><tr><th>#</th><th>Customer</th><th>Txn ID</th><th>Amount</th><th>Status</th><th>Gateway</th><th>Date</th></tr></thead>
                <tbody>
                  {txns.length === 0
                    ? <tr><td colSpan={7} className="empty">No transactions</td></tr>
                    : txns.map((t, i) => (
                      <tr key={t._id}>
                        <td>{(page-1)*LIMIT+i+1}</td>
                        <td><span className="customer-name">{t.customerName}</span><br/><span className="muted">{t.customerNumber}</span></td>
                        <td><span className="order-id">{t.txnId || t._id}</span></td>
                        <td><span className="amount">₹{fmt(t.amount)}</span></td>
                        <td><StatusBadge status={t.status} /></td>
                        <td><span className="pill-outline">{t.gatewayType || '—'}</span></td>
                        <td className="muted">{new Date(t.createdAt).toLocaleDateString('en-IN')}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <div className="pagination">
              <button className="btn btn-sm" disabled={page===1} onClick={() => setPage(p=>p-1)}>← Prev</button>
              <span className="page-info">Page {page} of {pages||1} ({total} total)</span>
              <button className="btn btn-sm" disabled={page>=pages} onClick={() => setPage(p=>p+1)}>Next →</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// LEDGER
// ════════════════════════════════════════════════════════════════════════════════
const Ledger = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoad]    = useState(true);
  const [page, setPage]       = useState(1);
  const [total, setTotal]     = useState(0);
  const [stats, setStats]     = useState({});
  const [dateFilter, setDate] = useState('today');
  const [search, setSearch]   = useState('');
  const LIMIT = 10;

  const ledgerFetch = useRef(null);
  ledgerFetch.current = async (p) => {
    setLoad(true);
    const params = new URLSearchParams({ page: p, limit: LIMIT });
    if (search) params.append('search', search);
    const dp = parseDateFilter(dateFilter);
    Object.entries(dp).forEach(([k,v]) => params.append(k, v));
    const data = await api(`/admin/wallet-ledger?${params}`);
    const d = data?.data || data;
    setEntries(d?.ledgerEntries || []);
    setTotal(d?.pagination?.totalEntries || 0);
    setStats({ totalAmount: d?.stats?.totalAmount??0, totalEntries: d?.pagination?.totalEntries??0, credits: d?.stats?.creditEntries??0, debits: d?.stats?.debitEntries??0 });
    setLoad(false);
  };

  const fetchLedger = useCallback((p) => ledgerFetch.current(p ?? page), [page]);
  useEffect(() => { ledgerFetch.current(page); }, [page]);
  useEffect(() => { setPage(1); ledgerFetch.current(1); }, [dateFilter, search]);
  const pages = Math.ceil(total / LIMIT);

  return (
    <div className="section">
      <div className="stat-grid">
        <StatCard label="Total Amount"  value={`₹${fmt(stats.totalAmount)}`} />
        <StatCard label="Total Entries" value={stats.totalEntries ?? total} />
        <StatCard label="Credits"       value={stats.credits ?? '—'} color="#34c89a" />
        <StatCard label="Debits"        value={stats.debits ?? '—'}  color="#e05c5c" />
      </div>
      <div className="card">
        <div className="filters-row">
          <input className="search-input" placeholder="Search by name, phone, UTR…"
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          <DateFilterBar value={dateFilter} onChange={v => { setDate(v); setPage(1); }} />
          <button className="btn" onClick={fetchLedger}>↻ Refresh</button>
        </div>
        {loading ? <div className="center"><Spinner /></div> : (
          <>
            <div className="table-wrap">
              <table>
                <thead><tr><th>#</th><th>User</th><th>Type</th><th>Amount</th><th>Balance After</th><th>Reference</th><th>Description</th><th>Date</th></tr></thead>
                <tbody>
                  {entries.length === 0
                    ? <tr><td colSpan={8} className="empty">No entries</td></tr>
                    : entries.map((e, i) => {
                      const meta = e.metadata || {};
                      return (
                        <tr key={e._id}>
                          <td>{(page-1)*LIMIT+i+1}</td>
                          <td><span className="customer-name">{e.userId?.name || '—'}</span><br/><span className="muted">{e.userId?.phone || e.userId?.email}</span></td>
                          <td><StatusBadge status={e.transactionType} /></td>
                          <td><span className={e.transactionType==='credit' ? 'amount-green' : 'amount-red'}>₹{fmt(e.amount)}</span></td>
                          <td className="muted">₹{fmt(e.balanceAfter)}</td>
                          <td className="muted order-id">{meta.orderId ? formatShortOrderId(meta.orderId) : meta.utr || e.referenceType || '—'}</td>
                          <td className="muted" style={{ maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{e.description || '—'}</td>
                          <td className="muted">{new Date(e.createdAt).toLocaleDateString('en-IN')}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
            <div className="pagination">
              <button className="btn btn-sm" disabled={page===1} onClick={() => setPage(p=>p-1)}>← Prev</button>
              <span className="page-info">Page {page} of {pages||1} ({total} total)</span>
              <button className="btn btn-sm" disabled={page>=pages} onClick={() => setPage(p=>p+1)}>Next →</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// USERS
// ════════════════════════════════════════════════════════════════════════════════
const Users = () => {
  const [users, setUsers]               = useState([]);
  const [loading, setLoad]              = useState(true);
  const [page, setPage]                 = useState(1);
  const [total, setTotal]               = useState(0);
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter]     = useState('');
  const [stats, setStats]               = useState({});
  const [editUser, setEditUser]         = useState(null);
  const [saving, setSaving]             = useState(false);
  const [saveMsg, setSaveMsg]           = useState('');
  const [form, setForm]                 = useState({});
  const LIMIT = 10;
  const [usersTab, setUsersTab]     = useState('list');
  const [suspicious, setSuspicious] = useState(null);
  const [suspLoading, setSuspLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoad(true);
    const params = new URLSearchParams({ page, limit: LIMIT });
    if (search)       params.append('search', search);
    if (statusFilter) params.append('status', statusFilter);
    if (roleFilter)   params.append('role', roleFilter);
    const data = await api(`/admin/users?${params}`);
    const d = data?.data || data;
    setUsers(d?.users || []);
    setTotal(d?.pagination?.totalUsers || 0);
    setStats(d?.stats || {});
    setLoad(false);
  }, [page, search, statusFilter, roleFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { setPage(1); }, [search, statusFilter, roleFilter]);

  const openEdit = (u) => {
    setEditUser(u);
    setForm({ name: u.name||'', email: u.email||'', status: u.status||'active', role: u.role||'user', verified: String(u.verified??false), walletBalance: u.walletBalance??0, password: '' });
    setSaveMsg('');
  };

  const saveUser = async () => {
    setSaving(true); setSaveMsg('');
    const payload = { name: form.name, email: form.email, status: form.status, role: form.role, verified: form.verified==='true', walletBalance: Number(form.walletBalance) };
    if (form.password) payload.password = form.password;
    try {
      const data = await api(`/admin/user/${editUser._id}`, { method:'PUT', body: JSON.stringify(payload) });
      if (data.success || data.data) { setSaveMsg('✅ Saved!'); fetchUsers(); setTimeout(() => setEditUser(null), 1200); }
      else setSaveMsg('❌ ' + (data.message || 'Failed'));
    } catch { setSaveMsg('❌ Network error'); }
    setSaving(false);
  };

  const quickBlock = async (u) => {
    await api(`/admin/users/${u._id}`, { method:'PUT', body: JSON.stringify({ status: u.status==='blocked' ? 'active' : 'blocked' }) });
    fetchUsers();
  };

  const fetchSuspicious = useCallback(async () => {
    setSuspLoading(true);
    try {
      const data = await api('/admin/suspicious-wallets');
      setSuspicious(data?.data || data);
    } catch(e) { console.error(e); }
    setSuspLoading(false);
  }, []);

  useEffect(() => {
    if (usersTab === 'suspicious' && !suspicious) fetchSuspicious();
  }, [usersTab, suspicious, fetchSuspicious]);

  const viewSuspiciousUser = async (userId) => {
    try {
      const data = await api(`/admin/user/${userId}`);
      const user = data?.data?.user || data?.data || data?.user;
      if (user?._id) openEdit(user);
    } catch(e) { console.error(e); }
  };

  const reasonBadge = (reason) => {
    if (!reason) return null;
    return reason.split(', ').map((r, i, arr) => (
      <span key={i} className={`badge ${r === 'High balance' ? 'badge-red' : 'badge-amber'}`}
        style={{ marginRight: i < arr.length - 1 ? 4 : 0 }}>
        <span className="dot" style={{ background: r === 'High balance' ? '#e05c5c' : '#f5a623' }} />
        {r}
      </span>
    ));
  };

  const pages = Math.ceil(total / LIMIT);
  const STATUS_TABS = [{ label:'All', value:'' }, { label:'Active', value:'active' }, { label:'Blocked', value:'blocked' }, { label:'Deleted', value:'deleted' }];

  return (
    <div className="section">
      <div className="tab-row" style={{ marginBottom: 12 }}>
        <button className={`tab-btn ${usersTab === 'list' ? 'tab-active' : ''}`}
          onClick={() => setUsersTab('list')}>👥 All Users</button>
        <button className={`tab-btn ${usersTab === 'suspicious' ? 'tab-active' : ''}`}
          onClick={() => { setUsersTab('suspicious'); if (!suspicious) fetchSuspicious(); }}>
          🚨 Suspicious Wallets
        </button>
      </div>

      {usersTab === 'list' && (<>
      <div className="stat-grid">
        <StatCard label="Total"       value={total} />
        <StatCard label="Active"      value={stats.regularUsers ?? '—'}    color="#34c89a" />
        <StatCard label="Verified"    value={stats.verifiedUsers ?? '—'}   color="#34c89a" />
        <StatCard label="Unverified"  value={stats.unverifiedUsers ?? '—'} color="#f5a623" />
        <StatCard label="Wallet Pool" value={stats.totalWalletBalance ? `₹${fmt(stats.totalWalletBalance)}` : '—'} />
        <StatCard label="Admins"      value={stats.adminUsers ?? '—'}      color="#9b6ff7" />
      </div>
      <div className="card">
        <div className="filters-row">
          <div style={{ display:'flex', gap:4 }}>
            {STATUS_TABS.map(t => (
              <button key={t.value} className={`tab-btn ${statusFilter===t.value ? 'tab-active' : ''}`}
                onClick={() => { setStatusFilter(t.value); setPage(1); }}>{t.label}</button>
            ))}
          </div>
          <input className="search-input" placeholder="Search name, email, phone…"
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          <select className="filter-select" value={roleFilter} onChange={e => { setRoleFilter(e.target.value); setPage(1); }}>
            <option value="">All roles</option>
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
          <button className="btn" onClick={fetchUsers}>↻ Refresh</button>
        </div>
        {loading ? <div className="center"><Spinner /></div> : (
          <>
            <div className="table-wrap">
              <table>
                <thead><tr><th>#</th><th>User</th><th>Phone</th><th>Wallet</th><th>Status</th><th>Verified</th><th>Role</th><th>Joined</th><th>Actions</th></tr></thead>
                <tbody>
                  {users.length === 0
                    ? <tr><td colSpan={9} className="empty">No users found</td></tr>
                    : users.map((u, i) => (
                      <tr key={u._id}>
                        <td>{(page-1)*LIMIT+i+1}</td>
                        <td>
                          <div className="user-row">
                            <div className="mini-avatar">{(u.name||u.email||'U')[0].toUpperCase()}</div>
                            <div><span className="customer-name">{u.name||'—'}</span><br/><span className="muted">{u.email}</span></div>
                          </div>
                        </td>
                        <td>{u.phone||'—'}</td>
                        <td><span className="amount">₹{fmt(u.walletBalance)}</span></td>
                        <td><StatusBadge status={u.status||'active'} /></td>
                        <td><StatusBadge status={u.verified ? 'verified' : 'unverified'} /></td>
                        <td><span className="pill-outline">{u.role||'user'}</span></td>
                        <td className="muted">{new Date(u.createdAt).toLocaleDateString('en-IN')}</td>
                        <td style={{ display:'flex', gap:4 }}>
                          <button className="btn-edit" onClick={() => openEdit(u)}>✏ Edit</button>
                          <button className="btn-edit" style={{ color: u.status==='blocked' ? '#34c89a' : '#e05c5c' }} onClick={() => quickBlock(u)}>
                            {u.status==='blocked' ? '✓ Unblock' : '⊘ Block'}
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <div className="pagination">
              <button className="btn btn-sm" disabled={page===1} onClick={() => setPage(p=>p-1)}>← Prev</button>
              <span className="page-info">Page {page} of {pages||1} ({total} total)</span>
              <button className="btn btn-sm" disabled={page>=pages} onClick={() => setPage(p=>p+1)}>Next →</button>
            </div>
          </>
        )}
      </div>

      </>)}

      {usersTab === 'suspicious' && (
        <div>
          {suspLoading ? (
            <div className="center"><Spinner /></div>
          ) : suspicious ? (
            <>
              <div className="stat-grid">
                <StatCard label="Avg Wallet Balance"  value={`₹${fmt(Math.round(suspicious.averageBalance ?? 0))}`} />
                <StatCard label="3× Threshold"        value={`₹${fmt(Math.round(suspicious.highBalanceThreshold ?? 0))}`} color="#f5a623" />
                <StatCard label="Flagged Users"       value={suspicious.count ?? 0} color="#e05c5c" />
              </div>
              <div className="card" style={{ marginTop: 12 }}>
                <div className="filters-row">
                  <div className="card-title" style={{ fontSize: 15, fontWeight: 600 }}>Flagged Users</div>
                  <button className="btn" onClick={fetchSuspicious}>↻ Refresh</button>
                </div>
                {(suspicious.users || []).length === 0 ? (
                  <div className="center" style={{ padding: 48, flexDirection: 'column', gap: 10 }}>
                    <div style={{ fontSize: 36 }}>✅</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>No suspicious wallets found</div>
                  </div>
                ) : (
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>User</th>
                          <th>Wallet Balance</th>
                          <th>Total Spent</th>
                          <th>Orders</th>
                          <th>Reason</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(suspicious.users || []).map((u, i) => (
                          <tr key={u.userId || i}>
                            <td>{i + 1}</td>
                            <td>
                              <span className="customer-name">{u.username}</span>
                              <br/>
                              <span className="muted">{u.email}</span>
                            </td>
                            <td><span className="amount">₹{fmt(u.walletBalance)}</span></td>
                            <td><span className="muted">₹{fmt(u.totalSpent)}</span></td>
                            <td>{u.orderCount}</td>
                            <td>{reasonBadge(u.reason)}</td>
                            <td>
                              <button className="btn-edit" onClick={() => viewSuspiciousUser(u.userId)}>
                                👤 View User
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="center" style={{ padding: 48 }}>
              <button className="btn btn-primary" onClick={fetchSuspicious}>Load Suspicious Wallets</button>
            </div>
          )}
        </div>
      )}

      {editUser && (
        <Modal title={`Edit — ${editUser.name || editUser.email}`} onClose={() => setEditUser(null)}>
          <div className="order-detail">
            <div className="detail-grid">
              <div><span className="detail-label">Name</span><input className="filter-select full-width" value={form.name} onChange={e => setForm(f=>({...f,name:e.target.value}))} /></div>
              <div><span className="detail-label">Email</span><input className="filter-select full-width" value={form.email} onChange={e => setForm(f=>({...f,email:e.target.value}))} /></div>
              <div>
                <span className="detail-label">Account status</span>
                <select className="filter-select full-width" value={form.status} onChange={e => setForm(f=>({...f,status:e.target.value}))}>
                  <option value="active">Active</option>
                  <option value="blocked">Blocked</option>
                  <option value="deleted">Deleted</option>
                </select>
              </div>
              <div>
                <span className="detail-label">Role</span>
                <select className="filter-select full-width" value={form.role} onChange={e => setForm(f=>({...f,role:e.target.value}))}>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <span className="detail-label">Verified</span>
                <select className="filter-select full-width" value={form.verified} onChange={e => setForm(f=>({...f,verified:e.target.value}))}>
                  <option value="true">Verified</option>
                  <option value="false">Unverified</option>
                </select>
              </div>
              <div><span className="detail-label">Wallet (₹)</span><input type="number" className="filter-select full-width" value={form.walletBalance} onChange={e => setForm(f=>({...f,walletBalance:e.target.value}))} /></div>
              <div className="full-row">
                <span className="detail-label">New password (blank = unchanged)</span>
                <input type="password" className="filter-select full-width" value={form.password} onChange={e => setForm(f=>({...f,password:e.target.value}))} placeholder="Enter new password…" />
              </div>
            </div>
            {saveMsg && <div className={`save-msg ${saveMsg.startsWith('✅') ? 'msg-success' : 'msg-error'}`} style={{ marginTop:12 }}>{saveMsg}</div>}
            <div className="modal-actions" style={{ marginTop:16 }}>
              <button className="btn" style={{ color: form.status==='blocked' ? '#34c89a' : '#e05c5c', marginRight:'auto' }}
                onClick={() => setForm(f=>({...f,status:f.status==='blocked'?'active':'blocked'}))}>
                {form.status==='blocked' ? '✓ Mark active' : '⊘ Block user'}
              </button>
              <button className="btn" onClick={() => setEditUser(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveUser} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// GAMES — Two tabs: Games + Diamond Packs
// ════════════════════════════════════════════════════════════════════════════════
const EMPTY_GAME_FORM = { name:'', publisher:'', category:'', image:'', productId:'', ogcode:'', deliveryType:'manual', deliveryMode:'username', botUsername:'', serverLink:'' };
const EMPTY_PACK_FORM = { description:'', amount:'', cost:'', commission:'', cashback:'0', status:'active', category:'default', rarity:'common', logo:'', game:'', verifiedOnly: false };

const Games = () => {
  const [activeTab, setActiveTab]     = useState('games');
  const [games, setGames]             = useState([]);
  const [packs, setPacks]             = useState([]);
  const [loadingG, setLoadG]          = useState(true);
  const [loadingP, setLoadP]          = useState(false);
  const [editGame, setEditGame]       = useState(null);
  const [editPack, setEditPack]       = useState(null);
  const [gForm, setGForm]             = useState(EMPTY_GAME_FORM);
  const [pForm, setPForm]             = useState(EMPTY_PACK_FORM);
  const [gImageFile, setGImageFile]   = useState(null);
  const [pImageFile, setPImageFile]   = useState(null);
  const [saving, setSaving]           = useState(false);
  const [msg, setMsg]                 = useState('');
  const [packMsg, setPackMsg]         = useState('');
  const [searchG, setSearchG]         = useState('');
  const [searchP, setSearchP]         = useState('');

  const fetchGames = useCallback(async () => {
    setLoadG(true);
    try {
      const data = await api('/games/get-all-admin');
      const list = data?.games || data?.data?.games || [];
      setGames(list);
      if (activeTab === 'packs') fetchPacks(list);
    } catch { setGames([]); }
    setLoadG(false);
  }, [activeTab]);

  const fetchPacks = useCallback(async (gameList) => {
    setLoadP(true);
    try {
      const list = gameList || games;
      if (!list.length) { setPacks([]); setLoadP(false); return; }
      const results = await Promise.all(list.map(g => api(`/games/${g._id}/diamond-packs`)));
      const allPacks = results.flatMap((data, i) => {
        const arr = data?.diamondPacks || data?.data?.diamondPacks || [];
        return arr.map(p => ({ ...p, _gameId: list[i]._id, _gameName: list[i].name }));
      });
      setPacks(allPacks);
    } catch { setPacks([]); }
    setLoadP(false);
  }, [games]);

  useEffect(() => { fetchGames(); }, [fetchGames]);
  useEffect(() => { if (activeTab === 'packs') fetchPacks(); }, [activeTab]);

  const openNewGame = () => { setEditGame({ _id: null }); setGForm(EMPTY_GAME_FORM); setGImageFile(null); setMsg(''); };
  const openEditGame = (g) => {
    setEditGame(g);
    setGForm({ name:g.name||'', publisher:g.publisher||'', category:g.category||'', image:g.image||'', productId:g.productId||'', ogcode:g.ogcode||'', deliveryType:g.deliveryType||'manual', deliveryMode:g.deliveryMode||'username', botUsername:g.botUsername||'', serverLink:g.serverLink||'' });
    setGImageFile(null); setMsg('');
  };

  const saveGame = async () => {
    if (!gForm.name?.trim()) { setMsg('❌ Game name is required'); return; }
    if (!editGame._id && !gForm.image?.trim() && !gImageFile) { setMsg('❌ Image is required'); return; }
    setSaving(true); setMsg('');
    try {
      const isNew = !editGame._id;
      const formData = new FormData();
      formData.append('name', gForm.name);
      formData.append('publisher', gForm.publisher || '');
      formData.append('category', gForm.category || '');
      formData.append('productId', gForm.productId || '');
      formData.append('ogcode', gForm.ogcode || '');
      formData.append('deliveryType', gForm.deliveryType || 'manual');
      formData.append('deliveryMode', gForm.deliveryMode || 'username');
      formData.append('botUsername', gForm.botUsername || '');
      formData.append('serverLink', gForm.serverLink || '');
      if (gImageFile) { formData.append('image', gImageFile); } else { formData.append('image', gForm.image || ''); }
      const res = await fetch(`${API_BASE}${isNew ? '/games/create' : `/games/${editGame._id}`}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
        body: formData,
      });
      const data = await res.json();
      if (res.ok) { setMsg('✅ Game saved!'); setGImageFile(null); fetchGames(); setTimeout(() => setEditGame(null), 1200); }
      else setMsg('❌ ' + (data.message || data.error || `Error ${res.status}`));
    } catch { setMsg('❌ Network error'); }
    setSaving(false);
  };

  const toggleGameActive = async (g) => {
    try {
      const res = await fetch(`${API_BASE}/games/${g._id}/toggle-active`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      if (res.ok) fetchGames();
      else { const d = await res.json(); alert(d.error || 'Toggle failed'); }
    } catch { alert('Network error'); }
  };

  const deleteGame = async (g) => {
    if (!window.confirm(`Delete "${g.name}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_BASE}/games/${g._id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });
      if (res.ok) fetchGames();
      else { const d = await res.json(); alert(d.message || 'Delete failed'); }
    } catch { alert('Network error'); }
  };

  const openNewPack = () => { setEditPack({ _id: null }); setPForm(EMPTY_PACK_FORM); setPImageFile(null); setPackMsg(''); };
  const openEditPack = (p) => {
    setEditPack(p);
    setPForm({ description:p.description||'', amount:p.amount||'', cost:p.cost ?? '', commission:p.commission||'', cashback:p.cashback||'0', status:p.status||'active', category:p.category||'default', rarity:p.rarity||'common', logo:p.logo||'', game:p._gameId||p.game?._id||p.game||'', verifiedOnly: p.verifiedOnly || false });
  };

  const savePack = async () => {
    if (!pForm.description?.trim() && !pForm.name?.trim()) { setPackMsg('❌ Pack name/description is required'); return; }
    if (!pForm.game) { setPackMsg('❌ Please select a game'); return; }
    setSaving(true); setPackMsg('');
    try {
      const isNew = !editPack._id;
      const formData = new FormData();
      formData.append('description', pForm.description || '');
      formData.append('amount', pForm.amount || '');
      formData.append('cost', pForm.cost || '0');
      formData.append('commission', pForm.commission || '');
      formData.append('cashback', pForm.cashback || '0');
      formData.append('status', pForm.status || 'active');
      formData.append('category', pForm.category || 'default');
      formData.append('rarity', pForm.rarity || 'common');
      formData.append('game', pForm.game);
      formData.append('verifiedOnly', pForm.verifiedOnly ? 'true' : 'false');
      formData.append('apiMappings', JSON.stringify([]));
      if (pImageFile) { formData.append('logo', pImageFile); } else { formData.append('logo', pForm.logo || ''); }
      const url = isNew ? `${API_BASE}/games/${pForm.game}/diamond-pack` : `${API_BASE}/games/diamond-pack/${editPack._id}`;
      const res = await fetch(url, { method: isNew ? 'POST' : 'PUT', headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }, body: formData });
      const data = await res.json();
      if (res.ok) { setPackMsg('✅ Pack saved!'); setPImageFile(null); fetchPacks(); setTimeout(() => setEditPack(null), 1200); }
      else setPackMsg('❌ ' + (data.message || data.error || `Error ${res.status}`));
    } catch { setPackMsg('❌ Network error'); }
    setSaving(false);
  };

  const deletePack = async (p) => {
    if (!window.confirm(`Delete pack "${p.description || p.name}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_BASE}/games/diamond-pack/${p._id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });
      if (res.ok) fetchPacks();
      else { const d = await res.json(); alert(d.message || 'Delete failed'); }
    } catch { alert('Network error'); }
  };

  const filteredGames = games.filter(g => !searchG || g.name?.toLowerCase().includes(searchG.toLowerCase()));
  const filteredPacks = packs.filter(p => !searchP || p.name?.toLowerCase().includes(searchP.toLowerCase()) || p.description?.toLowerCase().includes(searchP.toLowerCase()));

  return (
    <div className="section">
      <div className="tab-row">
        <button className={`tab-btn ${activeTab==='games' ? 'tab-active' : ''}`} onClick={() => setActiveTab('games')}>🎮 Games ({games.length})</button>
        <button className={`tab-btn ${activeTab==='packs' ? 'tab-active' : ''}`} onClick={() => setActiveTab('packs')}>💎 Diamond Packs ({packs.length})</button>
      </div>

      {activeTab === 'games' && (
        <div className="card" style={{ marginTop:12 }}>
          <div className="filters-row">
            <input className="search-input" placeholder="Search games…" value={searchG} onChange={e => setSearchG(e.target.value)} />
            <button className="btn btn-primary" onClick={openNewGame}>+ Add Game</button>
            <button className="btn" onClick={fetchGames}>↻ Refresh</button>
          </div>
          {loadingG ? <div className="center"><Spinner /></div> : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>#</th><th>Game</th><th>Category</th><th>Publisher</th><th>Actions</th></tr></thead>
                <tbody>
                  {filteredGames.length === 0
                    ? <tr><td colSpan={5} className="empty">No games found</td></tr>
                    : filteredGames.map((g, i) => (
                      <tr key={g._id} style={{ opacity: g.isActive === false ? 0.5 : 1 }}>
                        <td>{i+1}</td>
                        <td>
                          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                            {g.image && <img src={g.image} alt={g.name} style={{ width:36, height:36, borderRadius:8, objectFit:'cover' }} />}
                            <div>
                              <span className="customer-name">{g.name}</span>
                              {g.productId && <><br/><span className="muted">ID: {g.productId}</span></>}
                            </div>
                          </div>
                        </td>
                        <td><span className="pill-outline">{g.category||'—'}</span></td>
                        <td className="muted">{g.publisher||'—'}</td>
                        <td>
                          <button className="btn-edit" onClick={() => openEditGame(g)}>✏ Edit</button>
                          <button className="btn-edit" style={{ marginLeft:6, color: g.isActive === false ? '#34c89a' : '#f5a623' }} onClick={() => toggleGameActive(g)}>
                            {g.isActive === false ? '✓ Activate' : '⊘ Deactivate'}
                          </button>
                          <button className="btn-edit" style={{ marginLeft:6, color:'#e05c5c' }} onClick={() => deleteGame(g)}>🗑</button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'packs' && (
        <div className="card" style={{ marginTop:12 }}>
          <div className="filters-row">
            <input className="search-input" placeholder="Search packs…" value={searchP} onChange={e => setSearchP(e.target.value)} />
            <button className="btn btn-primary" onClick={openNewPack}>+ Add Pack</button>
            <button className="btn" onClick={fetchPacks}>↻ Refresh</button>
          </div>
          {loadingP ? <div className="center"><Spinner /></div> : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>#</th><th>Pack</th><th>Amount</th><th>Commission</th><th>Cashback</th><th>Game</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {filteredPacks.length === 0
                    ? <tr><td colSpan={8} className="empty">No packs found</td></tr>
                    : filteredPacks.map((p, i) => (
                      <tr key={p._id}>
                        <td>{i+1}</td>
                        <td>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            {p.logo && <img src={p.logo} alt="" style={{ width:28, height:28, borderRadius:6, objectFit:'cover' }} />}
                            <span className="customer-name">{p.description}</span>
                            {p.verifiedOnly && (
                              <span style={{
                                display: 'inline-block',
                                background: 'rgba(255,214,0,0.15)',
                                color: '#FFD600',
                                fontSize: '10px',
                                fontWeight: 700,
                                padding: '2px 8px',
                                borderRadius: '4px',
                                marginLeft: '8px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                              }}>
                                Verified
                              </span>
                            )}
                          </div>
                        </td>
                        <td><span className="amount">{p.amount}</span></td>
                        <td><span className="amount" style={{ color:'#34c89a' }}>₹{fmt(p.commission)}</span></td>
                        <td className="muted">₹{fmt(p.cashback)}</td>
                        <td className="muted">{p._gameName || p.game?.name || '—'}</td>
                        <td><StatusBadge status={p.status || 'active'} /></td>
                        <td>
                          <button className="btn-edit" onClick={() => openEditPack(p)}>✏ Edit</button>
                          <button className="btn-edit" style={{ marginLeft:6, color:'#e05c5c' }} onClick={() => deletePack(p)}>🗑</button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {editGame && (
        <Modal title={editGame._id ? 'Edit Game' : 'Add New Game'} onClose={() => setEditGame(null)}>
          <div className="order-detail">
            <div className="detail-grid">
              <div className="full-row">
                <span className="detail-label">Game name *</span>
                <input className="filter-select full-width" value={gForm.name} onChange={e => setGForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Blox Fruits" />
              </div>
              <div>
                <span className="detail-label">Publisher</span>
                <input className="filter-select full-width" value={gForm.publisher} onChange={e => setGForm(f=>({...f,publisher:e.target.value}))} placeholder="e.g. Roblox" />
              </div>
              <div>
                <span className="detail-label">Category</span>
                <input className="filter-select full-width" value={gForm.category} onChange={e => setGForm(f=>({...f,category:e.target.value}))} placeholder="e.g. RPG" />
              </div>
              <div>
                <span className="detail-label">Product ID</span>
                <input className="filter-select full-width" value={gForm.productId} onChange={e => setGForm(f=>({...f,productId:e.target.value}))} placeholder="e.g. bloxfruits" />
              </div>
              <div>
                <span className="detail-label">OG Code (for validation)</span>
                <input className="filter-select full-width" value={gForm.ogcode} onChange={e => setGForm(f=>({...f,ogcode:e.target.value}))} placeholder="e.g. ROBLOX" />
              </div>
              <div className="full-row">
                <ImagePicker label="Game Image *" currentUrl={gForm.image} onFileSelect={(file) => setGImageFile(file)} onUrlChange={(url) => setGForm(f => ({ ...f, image: url }))} />
              </div>
              <div className="full-row">
                <span className="detail-label">Delivery Type</span>
                <select className="filter-select full-width" value={gForm.deliveryType} onChange={e => setGForm(f=>({...f,deliveryType:e.target.value}))}>
                  <option value="manual">Manual</option>
                  <option value="semi-auto">Semi-Auto (Bot)</option>
                </select>
              </div>
              <div className="full-row">
                <span className="detail-label">Delivery Mode</span>
                <select className="filter-select full-width" value={gForm.deliveryMode} onChange={e => setGForm(f=>({...f,deliveryMode:e.target.value}))}>
                  <option value="username">Username (default)</option>
                  <option value="in_server_chat">In-Server Chat (agent delivery)</option>
                </select>
              </div>
              {gForm.deliveryType === 'semi-auto' && (
                <>
                  <div className="full-row">
                    <span className="detail-label">Bot Username (Roblox)</span>
                    <input className="filter-select full-width" value={gForm.botUsername} onChange={e => setGForm(f=>({...f,botUsername:e.target.value}))} placeholder="e.g. firegem123" />
                  </div>
                  <div className="full-row">
                    <span className="detail-label">Private Server Link</span>
                    <input className="filter-select full-width" value={gForm.serverLink} onChange={e => setGForm(f=>({...f,serverLink:e.target.value}))} placeholder="https://www.roblox.com/share?code=..." />
                  </div>
                </>
              )}
            </div>
            {msg && <div className={`save-msg ${msg.startsWith('✅') ? 'msg-success' : 'msg-error'}`} style={{ marginTop:12 }}>{msg}</div>}
            <div className="modal-actions" style={{ marginTop:16 }}>
              <button className="btn" onClick={() => setEditGame(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveGame} disabled={saving}>{saving ? 'Saving…' : 'Save Game'}</button>
            </div>
          </div>
        </Modal>
      )}

      {editPack && (
        <Modal title={editPack._id ? 'Edit Diamond Pack' : 'Add Diamond Pack'} onClose={() => setEditPack(null)}>
          <div className="order-detail">
            <div className="detail-grid">
              <div className="full-row">
                <span className="detail-label">Description / display name *</span>
                <input className="filter-select full-width" value={pForm.description} onChange={e => setPForm(f=>({...f,description:e.target.value}))} placeholder="e.g. 100 Beli" />
              </div>
              <div>
                <span className="detail-label">Amount (₹ selling price)</span>
                <input type="number" className="filter-select full-width" value={pForm.amount} onChange={e => setPForm(f=>({...f,amount:e.target.value}))} placeholder="e.g. 100" />
              </div>
              <div>
                <span className="detail-label">Cost (₹) — your buy cost</span>
                <input type="number" className="filter-select full-width" value={pForm.cost} onChange={e => setPForm(f=>({...f,cost:e.target.value}))} placeholder="e.g. 80" />
              </div>
              <div>
                <span className="detail-label">Commission (₹) — your profit</span>
                <input type="number" className="filter-select full-width" value={pForm.commission} onChange={e => setPForm(f=>({...f,commission:e.target.value}))} placeholder="auto = amount − cost" />
              </div>
              <div>
                <span className="detail-label">Cashback (₹)</span>
                <input type="number" className="filter-select full-width" value={pForm.cashback} onChange={e => setPForm(f=>({...f,cashback:e.target.value}))} placeholder="0" />
              </div>
              <div>
                <span className="detail-label">Category</span>
                <input className="filter-select full-width" value={pForm.category} onChange={e => setPForm(f=>({...f,category:e.target.value}))} placeholder="default" />
              </div>
              <div>
                <span className="detail-label">Rarity</span>
                <select className="filter-select full-width" value={pForm.rarity} onChange={e => setPForm(f=>({...f,rarity:e.target.value}))}>
                  <option value="common">Common</option>
                  <option value="uncommon">Uncommon</option>
                  <option value="rare">Rare</option>
                  <option value="epic">Epic</option>
                  <option value="legendary">Legendary</option>
                  <option value="mythical">Mythical</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <input
                    type="checkbox"
                    checked={!!pForm.verifiedOnly}
                    onChange={e => setPForm(f => ({ ...f, verifiedOnly: e.target.checked }))}
                    style={{ accentColor: '#FFD600' }}
                  />
                  <span style={{ fontSize: '14px' }}>Verified only (Verified Vault exclusive)</span>
                </label>
                <span className="detail-label">Status</span>
                <select className="filter-select full-width" value={pForm.status} onChange={e => setPForm(f=>({...f,status:e.target.value}))}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="full-row">
                <ImagePicker label="Pack Logo" currentUrl={pForm.logo} onFileSelect={(file) => setPImageFile(file)} onUrlChange={(url) => setPForm(f => ({ ...f, logo: url }))} />
              </div>
              <div className="full-row">
                <span className="detail-label">Game *</span>
                <select className="filter-select full-width" value={pForm.game} onChange={e => setPForm(f=>({...f,game:e.target.value}))}>
                  <option value="">— Select game —</option>
                  {games.map(g => <option key={g._id} value={g._id}>{g.name}</option>)}
                </select>
              </div>
            </div>
            {packMsg && <div className={`save-msg ${packMsg.startsWith('✅') ? 'msg-success' : 'msg-error'}`} style={{ marginTop:12 }}>{packMsg}</div>}
            <div className="modal-actions" style={{ marginTop:16 }}>
              <button className="btn" onClick={() => setEditPack(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={savePack} disabled={saving}>{saving ? 'Saving…' : 'Save Pack'}</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// WALLET ACTIONS
// ════════════════════════════════════════════════════════════════════════════════
const WalletActions = () => {
  const [type, setCType]            = useState('credit');
  const [phone, setPhone]           = useState('');
  const [amount, setAmount]         = useState('');
  const [utr, setUtr]               = useState('');
  const [creditType, setCreditType] = useState('add_balance');
  const [msg, setMsg]               = useState('');
  const [loading, setLoad]          = useState(false);

  const submit = async () => {
    if (!phone || !amount) { setMsg('❌ Fill all required fields'); return; }
    setLoad(true); setMsg('');
    try {
      const endpoint = type === 'credit' ? '/admin/credit-wallet' : '/admin/debit-wallet';
      const body = type === 'credit' ? { phone, amount: Number(amount), creditType, utr } : { phone, amount: Number(amount), utr };
      const data = await api(endpoint, { method:'POST', body: JSON.stringify(body) });
      setMsg(data.success || data.data ? `✅ ₹${amount} ${type}ed! New balance: ₹${fmt(data.data?.newBalance)}` : '❌ ' + (data.message || 'Failed'));
      if (data.success || data.data) { setPhone(''); setAmount(''); setUtr(''); }
    } catch { setMsg('❌ Network error'); }
    setLoad(false);
  };

  return (
    <div className="section">
      <div className="card wallet-card">
        <div className="card-title">Wallet transaction</div>
        <div className="type-toggle">
          <button className={`type-btn ${type==='credit' ? 'type-active-green' : ''}`} onClick={() => setCType('credit')}>✚ Credit</button>
          <button className={`type-btn ${type==='debit' ? 'type-active-red' : ''}`} onClick={() => setCType('debit')}>− Debit</button>
        </div>
        <div className="form-group"><label>Phone number *</label><input placeholder="User's phone" value={phone} onChange={e => setPhone(e.target.value)} /></div>
        <div className="form-group"><label>Amount (₹) *</label><input type="number" placeholder="0.00" value={amount} onChange={e => setAmount(e.target.value)} /></div>
        {type === 'credit' && (
          <div className="form-group">
            <label>Credit type</label>
            <select value={creditType} onChange={e => setCreditType(e.target.value)} style={{ width:'100%', padding:'8px 10px', borderRadius:8, border:'1px solid #ddd' }}>
              <option value="add_balance">Add balance</option>
              <option value="reward">Reward</option>
            </select>
          </div>
        )}
        <div className="form-group"><label>UTR / Reference (optional)</label><input placeholder="UTR number" value={utr} onChange={e => setUtr(e.target.value)} /></div>
        {msg && <div className={`save-msg ${msg.startsWith('✅') ? 'msg-success' : 'msg-error'}`}>{msg}</div>}
        <button className={`btn full-width ${type==='credit' ? 'btn-primary' : 'btn-danger'}`} onClick={submit} disabled={loading}>
          {loading ? 'Processing…' : `${type==='credit' ? '✚ Credit' : '− Debit'} Wallet`}
        </button>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// REPORTS
// ════════════════════════════════════════════════════════════════════════════════
const Reports = () => {
  const [activeTab, setActiveTab] = useState('buyers');
  const [timeframe, setTimeframe] = useState('30');
  const [topBuyers, setTopBuyers] = useState([]);
  const [topWallet, setTopWallet] = useState([]);
  const [revenue, setRevenue]     = useState(null);
  const [loading, setLoad]        = useState(true);

  const fetchReports = useCallback(async () => {
    setLoad(true);
    try {
      const [r, t, w] = await Promise.all([
        api(`/admin/analytics/profit?timeframe=${timeframe}&groupBy=day`),
        api(`/admin/top-buyers`),
        api(`/admin/top-wallet-adders`),
      ]);
      const rev = r?.data?.summary || r?.data || r;
      setRevenue(rev);
      setTopBuyers(t?.data?.topBuyers || t?.data?.buyers || t?.data || []);
      setTopWallet(w?.data?.topWalletAdders || w?.data?.walletAdders || w?.data || []);
    } catch(e) { console.error(e); }
    setLoad(false);
  }, [timeframe]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  return (
    <div className="section">
      <div className="filters-row" style={{ marginBottom:12 }}>
        <select className="filter-select" value={timeframe} onChange={e => setTimeframe(e.target.value)}>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
        </select>
        <button className="btn" onClick={fetchReports}>↻ Refresh</button>
      </div>
      {loading ? <div className="center"><Spinner /></div> : (
        <>
          <div className="stat-grid">
            <StatCard label="Total Revenue" value={`₹${fmt(revenue?.totalRevenue)}`}                      color="#34c89a" />
            <StatCard label="Total Profit"  value={`₹${fmt(revenue?.totalProfit)}`}                       color="#9b6ff7" />
            <StatCard label="Total Cost"    value={`₹${fmt(revenue?.totalCost)}`}                         color="#f5a623" />
            <StatCard label="Total Orders"  value={revenue?.totalOrders ?? 0} />
            <StatCard label="Avg Order"     value={`₹${fmt(Math.round(revenue?.averageOrderValue ?? 0))}`} />
            <StatCard label="Profit Margin" value={`${revenue?.overallProfitMargin ?? 0}%`}               color="#9b6ff7" />
          </div>
          <div className="card" style={{ marginTop:12 }}>
            <div className="tab-row">
              <button className={`tab-btn ${activeTab==='buyers' ? 'tab-active' : ''}`} onClick={() => setActiveTab('buyers')}>🏆 Top Buyers ({topBuyers.length})</button>
              <button className={`tab-btn ${activeTab==='wallet' ? 'tab-active' : ''}`} onClick={() => setActiveTab('wallet')}>👝 Top Wallet Adders ({topWallet.length})</button>
            </div>
            {activeTab === 'buyers' && (
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Rank</th><th>User</th><th>Phone</th><th>Total Spent</th><th>Orders</th><th>Last Order</th></tr></thead>
                  <tbody>
                    {topBuyers.length === 0 ? <tr><td colSpan={6} className="empty">No data</td></tr>
                      : topBuyers.map((u, i) => (
                        <tr key={u._id||i}>
                          <td><span className="rank" style={{ color: i===0?'#f5a623':i===1?'#aaa':i===2?'#c87a00':'inherit' }}>{i===0?'🥇':i===1?'🥈':i===2?'🥉':`#${i+1}`}</span></td>
                          <td><span className="customer-name">{u.userName || u.name}</span><br/><span className="muted">{u.userEmail || u.email}</span></td>
                          <td className="muted">{u.userPhone || u.phone}</td>
                          <td><span className="amount">₹{fmt(u.totalSpent)}</span></td>
                          <td>{u.totalOrders || u.orderCount}</td>
                          <td className="muted">{u.lastOrderDate ? new Date(u.lastOrderDate).toLocaleDateString('en-IN') : '—'}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
            {activeTab === 'wallet' && (
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Rank</th><th>User</th><th>Phone</th><th>Total Added</th><th>Transactions</th><th>Last Added</th></tr></thead>
                  <tbody>
                    {topWallet.length === 0 ? <tr><td colSpan={6} className="empty">No data</td></tr>
                      : topWallet.map((u, i) => (
                        <tr key={u._id||i}>
                          <td><span className="rank" style={{ color: i===0?'#f5a623':i===1?'#aaa':i===2?'#c87a00':'inherit' }}>{i===0?'🥇':i===1?'🥈':i===2?'🥉':`#${i+1}`}</span></td>
                          <td><span className="customer-name">{u.userName || u.name}</span><br/><span className="muted">{u.userEmail || u.email}</span></td>
                          <td className="muted">{u.userPhone || u.phone}</td>
                          <td><span className="amount">₹{fmt(u.totalCredited || u.totalAdded)}</span></td>
                          <td>{u.totalTransactions || u.transactionCount}</td>
                          <td className="muted">{u.lastCreditDate ? new Date(u.lastCreditDate).toLocaleDateString('en-IN') : '—'}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// COUPONS — fixed field names, onePerCustomer, excludedGames UI
// ════════════════════════════════════════════════════════════════════════════════
const BLANK_COUPON = {
  code: '', discountType: 'percentage', discountValue: '', cap: '',
  minPurchase: '0', maxUsage: '', onePerCustomer: true,
  startDate: '', endDate: '', status: 'active',
  excludedGames: [], excludedDiamondPacks: [],
};

const Coupons = () => {
  const [coupons, setCoupons]   = useState([]);
  const [loading, setLoad]      = useState(true);
  const [search, setSearch]     = useState('');
  const [statusF, setStatusF]   = useState('');
  const [editC, setEditC]       = useState(null);
  const [form, setForm]         = useState(BLANK_COUPON);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState('');
  const [statsC, setStatsC]     = useState({});
  const [games, setGames]       = useState([]);
  const [allPacks, setAllPacks] = useState([]);
  const [viewC, setViewC]       = useState(null);
  const [viewStats, setViewStats] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const fetchCoupons = useCallback(async () => {
    setLoad(true);
    try {
      const data = await api('/admin/coupons?page=1&limit=200');
      const list = data?.data?.coupons || data?.coupons || data?.data || [];
      let merged = list;
      try {
        const statsData = await api('/admin/coupon-stats');
        const statsList = statsData?.coupons || [];
        const statsById = new Map(statsList.map(s => [s._id?.toString(), s]));
        merged = list.map(c => {
          const s = statsById.get((c._id || c.id)?.toString());
          return s ? { ...c, totalRevenue: s.totalRevenue, totalDiscount: s.totalDiscount, uniqueUsers: s.uniqueUsers } : c;
        });
      } catch { /* revenue columns stay blank if stats fetch fails */ }
      setCoupons(merged);
      const active = list.filter(c => (c.status || 'active') === 'active').length;
      setStatsC({ active, total: list.length });
    } catch { setCoupons([]); }
    setLoad(false);
  }, []);

  const openView = async (c) => {
    setViewC(c);
    setViewStats(null);
    setViewLoading(true);
    try {
      const data = await api(`/admin/coupon-stats/${c._id || c.id}`);
      setViewStats(data?.stats || null);
    } catch { setViewStats(null); }
    setViewLoading(false);
  };

  // Load games for the exclude games section
  useEffect(() => {
  fetchCoupons();
  api('/games/get-all').then(async data => {
    const gameList = data?.games || data?.data?.games || [];
    setGames(gameList);
    if (!gameList.length) return;
    const results = await Promise.all(gameList.map(g => api(`/games/${g._id}/diamond-packs`)));
    const packs = results.flatMap((res, i) => {
      const arr = res?.diamondPacks || res?.data?.diamondPacks || [];
      return arr.map(p => ({ ...p, _gameId: gameList[i]._id, _gameName: gameList[i].name }));
    });
    console.log('Loaded packs:', packs);
    setAllPacks(packs);
  });
}, [fetchCoupons]);

  const openNew = () => {
    setForm({ ...BLANK_COUPON });
    setEditC({ _id: null });
    setMsg('');
  };

  const openEdit = (c) => {
    setForm({
      code: c.code || '',
      discountType: c.discountType || 'percentage',
      discountValue: c.discountValue ?? c.discount ?? '',
      cap: c.maxDiscountAmount ?? '',
      minPurchase: c.minPurchaseAmount ?? '0',
      maxUsage: c.totalUsageLimit ?? '',
      onePerCustomer: c.onePerCustomer !== false,
      startDate: c.startDate ? c.startDate.split('T')[0] : '',
      endDate: c.endDate ? c.endDate.split('T')[0] : '',
      status: c.status || 'active',
      excludedGames: (c.excludedGames || []).map(g => typeof g === 'object' ? g._id : g),
      excludedDiamondPacks: (c.excludedDiamondPacks || []).map(p => typeof p === 'object' ? p._id : p),
    });
    setEditC({ ...c, _id: c._id?.toString() || c.id?.toString() });
    setMsg('');
  };

    const saveCoupon = async () => {
    console.log('editC:', editC);
    console.log('editC._id:', editC._id);
    console.log('editC.id:', editC.id);
    if (!form.code?.trim()) { setMsg('❌ Code is required'); return; }
    if (!form.discountValue) { setMsg('❌ Discount value is required'); return; }
    setSaving(true); setMsg('');
    try {
      const isNew = !editC._id;
      const payload = {
        code: form.code.toUpperCase(),
        discountType: form.discountType,
        discountValue: Number(form.discountValue),
        minPurchaseAmount: Number(form.minPurchase || 0),
        maxDiscountAmount: form.cap ? Number(form.cap) : undefined,
        totalUsageLimit: form.maxUsage ? Number(form.maxUsage) : 100,
        onePerCustomer: form.onePerCustomer,
        startDate: form.startDate,
        endDate: form.endDate,
        status: form.status,
        excludedGames: form.excludedGames || [],
        excludedDiamondPacks: form.excludedDiamondPacks || [],
        excludedCategories: [],
      };
      const couponId = editC._id || editC.id;
      const res = await fetch(`${API_BASE}${isNew ? '/admin/coupons' : `/admin/coupons/${couponId}`}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) { setMsg('✅ Saved!'); fetchCoupons(); setTimeout(() => setEditC(null), 1200); }
      else setMsg('❌ ' + (data.message || data.error || `Error ${res.status}`));
    } catch { setMsg('❌ Network error'); }
    setSaving(false);
  };

  const deleteCoupon = async (c) => {
    if (!window.confirm(`Delete coupon "${c.code}"?`)) return;
    try {
      const res = await fetch(`${API_BASE}/admin/coupons/${c._id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      if (res.ok) fetchCoupons();
      else { const d = await res.json(); alert(d.message || 'Delete failed'); }
    } catch { alert('Network error'); }
  };

  const toggleExcludedGame = (gameId) => {
  const ids = form.excludedGames.includes(gameId)
    ? form.excludedGames.filter(id => id !== gameId)
    : [...form.excludedGames, gameId];
  setForm(f => ({ ...f, excludedGames: ids }));
};

const toggleExcludedPack = (packId) => {
  const ids = form.excludedDiamondPacks.includes(packId)
    ? form.excludedDiamondPacks.filter(id => id !== packId)
    : [...form.excludedDiamondPacks, packId];
  setForm(f => ({ ...f, excludedDiamondPacks: ids }));
};

  const fmtDiscount = (c) => {
    const val = c.discountValue ?? c.discount;
    if (val === undefined || val === null || val === '') return '—';
    const disp = c.discountType === 'percentage' ? `${val}%` : `₹${fmt(val)}`;
    return c.maxDiscountAmount ? `${disp} (cap ₹${fmt(c.maxDiscountAmount)})` : disp;
  };
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';

  const filtered = coupons.filter(c => {
    const matchStatus = !statusF || (c.status || 'active') === statusF;
    const matchSearch = !search || c.code?.toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  return (
    <div className="section">
      <div className="stat-grid">
        <StatCard label="Active Coupons" value={statsC.active ?? '—'} color="#22c55e" />
        <StatCard label="Total Loaded"   value={statsC.total  ?? '—'} />
      </div>
      <div className="card">
        <div className="filters-row">
          <select className="filter-select" value={statusF} onChange={e => setStatusF(e.target.value)}>
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="expired">Expired</option>
          </select>
          <input className="search-input" placeholder="Search by code…" value={search} onChange={e => setSearch(e.target.value)} />
          <button className="btn" onClick={fetchCoupons}>↻ Refresh</button>
          <button className="btn btn-primary" onClick={openNew}>+ Create Coupon</button>
        </div>
        {loading ? <div className="center"><Spinner /></div> : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Code</th><th>Discount</th><th>Min Purchase</th><th>Usage</th><th>Revenue</th><th>Discount Given</th><th>Unique Users</th><th>Per Customer</th><th>Date Range</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filtered.length === 0
                  ? <tr><td colSpan={11} className="empty">No coupons found</td></tr>
                  : filtered.map(c => (
                    <tr key={c._id} style={{ cursor:'pointer' }} onClick={() => openView(c)}>
                      <td><span className="coupon-code">{c.code}</span></td>
                      <td><span style={{ fontWeight:600, color:'var(--text-primary)' }}>{fmtDiscount(c)}</span><br/><span className="muted">{c.discountType === 'percentage' ? 'percentage' : 'flat'}</span></td>
                      <td>₹{fmt(c.minPurchaseAmount ?? 0)}</td>
                      <td>
                        <span style={{ fontWeight:600 }}>{c.usedCount ?? 0}</span>
                        <span className="muted"> / {c.totalUsageLimit ?? '∞'}</span>
                      </td>
                      <td>{c.totalRevenue !== undefined ? `₹${fmt(c.totalRevenue)}` : '—'}</td>
                      <td>{c.totalDiscount !== undefined ? `₹${fmt(c.totalDiscount)}` : '—'}</td>
                      <td>{c.uniqueUsers ?? '—'}</td>
                      <td>
                        <span className={`badge ${c.onePerCustomer ? 'badge-blue' : 'badge-gray'}`}>
                          <span className="dot" style={{ background: c.onePerCustomer ? '#3a8ef6' : '#aaa' }} />
                          {c.onePerCustomer ? 'One-time' : 'Multi-use'}
                        </span>
                      </td>
                      <td className="muted" style={{ fontSize:12 }}>{fmtDate(c.startDate)}<br/>→ {fmtDate(c.endDate)}</td>
                      <td><StatusBadge status={c.status || 'active'} /></td>
                      <td style={{ display:'flex', gap:6, flexWrap:'wrap' }} onClick={e => e.stopPropagation()}>
                        <button className="btn-edit" onClick={() => openView(c)}>📊 Stats</button>
                        <button className="btn-edit" onClick={() => openEdit(c)}>✏ Edit</button>
                        <button className="btn-edit" style={{ color:'var(--red)' }} onClick={() => deleteCoupon(c)}>🗑</button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editC && (
        <Modal title={editC._id ? `Edit — ${editC.code}` : 'Create Coupon'} onClose={() => setEditC(null)}>
          <div className="order-detail">
            <div className="detail-grid">

              <div className="full-row">
                <span className="detail-label">Coupon Code *</span>
                <input className="filter-select full-width" style={{ textTransform:'uppercase', fontFamily:'monospace', fontWeight:700, letterSpacing:1 }}
                  value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                  placeholder="e.g. SAVE20" disabled={!!editC._id} />
              </div>

              <div>
                <span className="detail-label">Discount Type</span>
                <select className="filter-select full-width" value={form.discountType} onChange={e => setForm(f => ({ ...f, discountType: e.target.value }))}>
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Flat (₹)</option>
                </select>
              </div>

              <div>
                <span className="detail-label">Discount Value *</span>
                <input type="number" className="filter-select full-width" value={form.discountValue}
                  onChange={e => setForm(f => ({ ...f, discountValue: e.target.value }))}
                  placeholder={form.discountType === 'percentage' ? 'e.g. 5' : 'e.g. 50'} />
              </div>

              {form.discountType === 'percentage' && (
                <div>
                  <span className="detail-label">Max Discount Cap (₹)</span>
                  <input type="number" className="filter-select full-width" value={form.cap}
                    onChange={e => setForm(f => ({ ...f, cap: e.target.value }))} placeholder="e.g. 80 (optional)" />
                </div>
              )}

              <div>
                <span className="detail-label">Min Purchase (₹)</span>
                <input type="number" className="filter-select full-width" value={form.minPurchase}
                  onChange={e => setForm(f => ({ ...f, minPurchase: e.target.value }))} placeholder="0" />
              </div>

              <div>
                <span className="detail-label">Max Total Uses</span>
                <input type="number" className="filter-select full-width" value={form.maxUsage}
                  onChange={e => setForm(f => ({ ...f, maxUsage: e.target.value }))} placeholder="blank = 100" />
              </div>

              <div className="full-row">
                <span className="detail-label">One Use Per Customer</span>
                <select className="filter-select full-width" value={String(form.onePerCustomer)}
                  onChange={e => setForm(f => ({ ...f, onePerCustomer: e.target.value === 'true' }))}>
                  <option value="true">Yes — one use per customer</option>
                  <option value="false">No — unlimited uses per customer</option>
                </select>
              </div>

              <div>
                <span className="detail-label">Start Date</span>
                <input type="date" className="filter-select full-width" value={form.startDate}
                  onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} />
              </div>

              <div>
                <span className="detail-label">End Date</span>
                <input type="date" className="filter-select full-width" value={form.endDate}
                  onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} />
              </div>

              <div className="full-row">
                <span className="detail-label">Status</span>
                <select className="filter-select full-width" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

         {/* ── Exclude Games Section ── */}
              <div className="full-row">
                <span className="detail-label">Exclude Games (coupon won't apply to these)</span>
                {games.length === 0 ? (
                  <span className="muted" style={{ fontSize:12 }}>No games found</span>
                ) : (
                  <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:6 }}>
                    {games.map(g => {
                      const isExcluded = form.excludedGames.includes(g._id);
                      return (
                        <label key={g._id} style={{
                          display:'flex', alignItems:'center', gap:6, cursor:'pointer',
                          background: isExcluded ? '#e05c5c22' : 'var(--bg-elevated)',
                          border: `1px solid ${isExcluded ? '#e05c5c' : 'var(--border)'}`,
                          borderRadius:6, padding:'5px 10px', fontSize:12, userSelect:'none',
                          transition:'all .15s'
                        }}>
                          <input type="checkbox" checked={isExcluded}
                            onChange={() => toggleExcludedGame(g._id)}
                            style={{ accentColor:'#e05c5c' }} />
                          {g.image && <img src={g.image} alt="" style={{ width:16, height:16, borderRadius:3, objectFit:'cover' }} />}
                          {g.name}
                        </label>
                      );
                    })}
                  </div>
                )}
                {form.excludedGames.length > 0 && (
                  <div style={{ marginTop:6, fontSize:11, color:'var(--text-muted)' }}>
                    {form.excludedGames.length} game(s) excluded
                    <button onClick={() => setForm(f => ({ ...f, excludedGames: [] }))}
                      style={{ marginLeft:8, background:'none', border:'none', color:'#e05c5c', cursor:'pointer', fontSize:11 }}>
                      Clear all
                    </button>
                  </div>
                )}
              </div>

              {/* ── Exclude Products Section ── */}
              <div className="full-row">
                <span className="detail-label">Exclude Products (coupon won't apply to these)</span>
                {allPacks.length === 0 ? (
                  <span className="muted" style={{ fontSize:12 }}>No products found</span>
                ) : (
                  <div style={{ maxHeight:200, overflowY:'auto', border:'1px solid var(--border)', borderRadius:8, padding:10, marginTop:6 }}>
                    {games.map(g => {
                      const gamePacks = allPacks.filter(p => p._gameId?.toString() === g._id?.toString());
                      if (!gamePacks.length) return null;
                      return (
                        <div key={g._id} style={{ marginBottom:10 }}>
                          <div style={{ fontSize:11, fontWeight:700, color:'var(--text-muted)', marginBottom:4, textTransform:'uppercase', letterSpacing:1 }}>
                            {g.name}
                          </div>
                          <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                            {gamePacks.map(p => {
                              const isExcluded = form.excludedDiamondPacks.includes(p._id);
                              return (
                                <label key={p._id} style={{
                                  display:'flex', alignItems:'center', gap:4, cursor:'pointer',
                                  background: isExcluded ? '#e05c5c22' : 'var(--bg-elevated)',
                                  border: `1px solid ${isExcluded ? '#e05c5c' : 'var(--border)'}`,
                                  borderRadius:6, padding:'4px 8px', fontSize:11, userSelect:'none', transition:'all .15s'
                                }}>
                                  <input type="checkbox" checked={isExcluded}
                                    onChange={() => toggleExcludedPack(p._id)}
                                    style={{ accentColor:'#e05c5c' }} />
                                  {p.description} {p.amount ? `(₹${p.amount})` : ''}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {form.excludedDiamondPacks.length > 0 && (
                  <div style={{ marginTop:6, fontSize:11, color:'var(--text-muted)' }}>
                    {form.excludedDiamondPacks.length} product(s) excluded
                    <button onClick={() => setForm(f => ({ ...f, excludedDiamondPacks: [] }))}
                      style={{ marginLeft:8, background:'none', border:'none', color:'#e05c5c', cursor:'pointer', fontSize:11 }}>
                      Clear all
                    </button>
                  </div>
                )}
              </div>

            </div>

            {msg && <div className={`save-msg ${msg.startsWith('✅') ? 'msg-success' : 'msg-error'}`}>{msg}</div>}
            <div className="modal-actions">
              <button className="btn" onClick={() => setEditC(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveCoupon} disabled={saving}>{saving ? 'Saving…' : editC._id ? 'Update Coupon' : 'Create Coupon'}</button>
            </div>
          </div>
        </Modal>
      )}

      {viewC && (
        <Modal title={`Stats — ${viewC.code}`} onClose={() => setViewC(null)}>
          {viewLoading ? <div className="center"><Spinner /></div> : !viewStats ? (
            <div className="empty">Failed to load coupon stats</div>
          ) : (
            <div className="order-detail">
              <div className="stat-grid">
                <StatCard label="Total Order Value" value={`₹${fmt(viewStats.revenue?.totalOrderValue)}`} />
                <StatCard label="Total Discount Given" value={`₹${fmt(viewStats.revenue?.totalDiscountGiven)}`} color="#e05c5c" />
                <StatCard label="Net Revenue" value={`₹${fmt(viewStats.revenue?.totalRevenueAfterDiscount)}`} color="#22c55e" />
                <StatCard label="Avg Order Value" value={`₹${fmt(Math.round(viewStats.revenue?.averageOrderValue || 0))}`} />
              </div>
              <div className="table-wrap" style={{ maxHeight: 360, overflowY: 'auto', marginTop: 16 }}>
                <table>
                  <thead>
                    <tr><th>Name</th><th>Email</th><th>Order ID</th><th>Order Amount</th><th>Discount</th><th>Final Paid</th><th>Date</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {(!viewStats.users || viewStats.users.length === 0)
                      ? <tr><td colSpan={8} className="empty">No usage yet</td></tr>
                      : viewStats.users.map((u, i) => (
                        <tr key={u.orderId || i}>
                          <td>{u.name}</td>
                          <td className="muted">{u.email}</td>
                          <td>{formatShortOrderId(u.orderId)}</td>
                          <td>₹{fmt(u.orderAmount)}</td>
                          <td>₹{fmt(u.discountAmount)}</td>
                          <td>₹{fmt(u.finalPaid)}</td>
                          <td className="muted" style={{ fontSize:12 }}>{fmtDate(u.date)}</td>
                          <td><StatusBadge status={u.status} /></td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// GATEWAYS
// ════════════════════════════════════════════════════════════════════════════════
const Gateways = () => {
  const [gateways, setGateways]   = useState([]);
  const [active, setActive]       = useState('');
  const [loading, setLoad]        = useState(true);
  const [editGw, setEditGw]       = useState(null);
  const [addMode, setAddMode]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [switching, setSwitching] = useState('');
  const [msg, setMsg]             = useState('');
  const [form, setForm]           = useState({});

  const BLANK_FORM = {
    gatewayId: '', name: '', description: '',
    apiKey: '', apiUrl: '', statusUrl: '',
    webhookUrl: '', merchantName: '', upiId: '',
    secretKey: '', userToken: '', enabled: true
  };

  const fetchGateways = useCallback(async () => {
    setLoad(true);
    const data = await api('/admin/settings/payment-gateways');
    const d = data?.data || data;
    setGateways(d?.gateways || []);
    setActive(d?.activeGateway || '');
    setLoad(false);
  }, []);

  useEffect(() => { fetchGateways(); }, [fetchGateways]);

  const openEdit = (gw) => {
    api(`/admin/settings/gateways/${gw._id}`).then(data => {
      const g = data?.data || gw;
      setForm({
        gatewayId: g.gatewayId || '', name: g.name || '', description: g.description || '',
        apiKey: g.apiKey || '', apiUrl: g.apiUrl || '', statusUrl: g.statusUrl || '',
        webhookUrl: g.webhookUrl || '', merchantName: g.merchantName || '', upiId: g.upiId || '',
        secretKey: g.secretKey || '', userToken: g.userToken || '', enabled: g.enabled !== false,
      });
      setEditGw(g); setMsg('');
    });
  };

  const openAdd = () => { setForm({ ...BLANK_FORM }); setAddMode(true); setMsg(''); };

  const saveGateway = async () => {
    setSaving(true); setMsg('');
    try {
      const isNew = addMode;
      const url = isNew ? '/admin/settings/gateways' : `/admin/settings/gateways/${editGw._id}`;
      const data = await api(url, { method: isNew ? 'POST' : 'PATCH', body: JSON.stringify(form) });
      if (data.success || data.data) {
        setMsg('✅ Gateway saved successfully!');
        fetchGateways();
        setTimeout(() => { setEditGw(null); setAddMode(false); }, 1200);
      } else setMsg('❌ ' + (data.message || 'Save failed'));
    } catch { setMsg('❌ Network error'); }
    setSaving(false);
  };

  const setActiveGateway = async (gatewayId) => {
    setSwitching(gatewayId);
    const data = await api('/admin/settings/payment-gateway', { method: 'PUT', body: JSON.stringify({ activeGateway: gatewayId }) });
    if (data.success) { setActive(gatewayId); fetchGateways(); }
    else alert('Failed: ' + (data.message || 'Unknown error'));
    setSwitching('');
  };

  const toggleEnabled = async (gw) => {
    await api(`/admin/settings/gateways/${gw._id}/toggle`, { method: 'PATCH' });
    fetchGateways();
  };

  const deleteGateway = async (gw) => {
    if (!window.confirm(`Delete "${gw.name}"? This cannot be undone.`)) return;
    const data = await api(`/admin/settings/gateways/${gw._id}`, { method: 'DELETE' });
    if (data.success) fetchGateways();
    else alert('Failed: ' + (data.message || 'Cannot delete active gateway'));
  };

  const GATEWAY_INFO = {
    mobagateway:      { label: 'Moba Gateway',     color: '#9b6ff7', icon: '💜' },
    onegateway:       { label: 'One Gateway',       color: '#3a8ef6', icon: '🔵' },
    ekqr:             { label: 'EKQR',              color: '#f5a623', icon: '🟡' },
    cgateway:         { label: 'C-Gateway',         color: '#34c89a', icon: '🟢' },
    connectedgateway: { label: 'Connected Gateway', color: '#e05c5c', icon: '🔴' },
  };

  return (
    <div className="section">
      {active && (
        <div style={{ background: '#e8f5e9', border: '1.5px solid #34c89a', borderRadius: 10, padding: '12px 18px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20 }}>✅</span>
          <div>
            <div style={{ fontWeight: 600, color: '#1a8a55' }}>Active Gateway: {GATEWAY_INFO[active]?.label || active}</div>
            <div style={{ fontSize: 12, color: '#555' }}>All payments are currently going through this gateway</div>
          </div>
        </div>
      )}
      <div className="card">
        <div className="filters-row">
          <div className="card-title" style={{ fontSize: 15, fontWeight: 600 }}>Payment Gateways ({gateways.length})</div>
          <button className="btn btn-primary" onClick={openAdd}>+ Add Gateway</button>
          <button className="btn" onClick={fetchGateways}>↻ Refresh</button>
        </div>
        {loading ? <div className="center"><Spinner /></div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '12px 16px' }}>
            {gateways.length === 0 ? (
              <p className="empty">No gateways found. Add one to get started.</p>
            ) : gateways.map(gw => {
              const info = GATEWAY_INFO[gw.gatewayId] || { label: gw.name, color: '#aaa', icon: '⚙️' };
              const isActiveGw = gw.gatewayId === active;
              return (
                <div key={gw._id} style={{ border: isActiveGw ? '2px solid #34c89a' : '1px solid #e8e8e8', borderRadius: 10, padding: '16px 20px', background: isActiveGw ? '#f0faf5' : '#fff', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 200 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 10, background: info.color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{info.icon}</div>
                    <div><div style={{ fontWeight: 600, fontSize: 15 }}>{gw.name}</div><div style={{ fontSize: 12, color: '#888' }}>{gw.gatewayId}</div></div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1 }}>
                    {isActiveGw && <span style={{ background: '#34c89a22', color: '#0f6e56', border: '1px solid #34c89a55', borderRadius: 99, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>✅ ACTIVE</span>}
                    <span style={{ background: gw.enabled ? '#e8f5e9' : '#fafafa', color: gw.enabled ? '#1a8a55' : '#999', border: `1px solid ${gw.enabled ? '#34c89a55' : '#ddd'}`, borderRadius: 99, padding: '3px 10px', fontSize: 12 }}>{gw.enabled ? 'Enabled' : 'Disabled'}</span>
                    <span style={{ background: gw.apiKey ? '#e8f0fe' : '#fff3e0', color: gw.apiKey ? '#185fa5' : '#e65100', border: `1px solid ${gw.apiKey ? '#378add55' : '#ff980055'}`, borderRadius: 99, padding: '3px 10px', fontSize: 12 }}>{gw.apiKey ? '🔑 API Key Set' : '⚠️ No API Key'}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {!isActiveGw && gw.enabled && (
                      <button className="btn btn-primary" style={{ fontSize: 12, height: 30, padding: '0 12px' }} onClick={() => setActiveGateway(gw.gatewayId)} disabled={switching === gw.gatewayId}>
                        {switching === gw.gatewayId ? 'Switching…' : '⚡ Set Active'}
                      </button>
                    )}
                    <button className="btn-edit" onClick={() => openEdit(gw)}>✏ Edit</button>
                    <button className="btn-edit" style={{ color: gw.enabled ? '#e05c5c' : '#34c89a' }} onClick={() => toggleEnabled(gw)}>{gw.enabled ? '⊘ Disable' : '✓ Enable'}</button>
                    {!isActiveGw && <button className="btn-edit" style={{ color: '#e05c5c' }} onClick={() => deleteGateway(gw)}>🗑 Delete</button>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {(editGw || addMode) && (
        <Modal title={addMode ? 'Add New Gateway' : `Edit — ${editGw?.name}`} onClose={() => { setEditGw(null); setAddMode(false); }}>
          <div className="order-detail">
            <div className="detail-grid">
              <div>
                <span className="detail-label">Gateway ID *</span>
                {addMode ? (
                  <select className="filter-select full-width" value={form.gatewayId} onChange={e => setForm(f => ({ ...f, gatewayId: e.target.value }))}>
                    <option value="">— Select —</option>
                    <option value="mobagateway">mobagateway</option>
                    <option value="onegateway">onegateway</option>
                    <option value="ekqr">ekqr</option>
                    <option value="cgateway">cgateway</option>
                    <option value="connectedgateway">connectedgateway</option>
                  </select>
                ) : (
                  <div style={{ padding: '8px 10px', background: '#f5f5f3', borderRadius: 8, fontWeight: 600, color: '#555' }}>{form.gatewayId}</div>
                )}
              </div>
              <div><span className="detail-label">Display Name *</span><input className="filter-select full-width" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Moba Gateway" /></div>
              <div className="full-row"><span className="detail-label">Description</span><input className="filter-select full-width" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Short description" /></div>
              <div className="full-row"><span className="detail-label">API Key *</span><input className="filter-select full-width" value={form.apiKey} onChange={e => setForm(f => ({ ...f, apiKey: e.target.value }))} placeholder="Your API key" /></div>
              <div><span className="detail-label">Merchant Name</span><input className="filter-select full-width" value={form.merchantName} onChange={e => setForm(f => ({ ...f, merchantName: e.target.value }))} placeholder="Your store name" /></div>
              <div><span className="detail-label">UPI ID</span><input className="filter-select full-width" value={form.upiId} onChange={e => setForm(f => ({ ...f, upiId: e.target.value }))} placeholder="yourstore@upi" /></div>
              <div className="full-row"><span className="detail-label">API URL (create payment)</span><input className="filter-select full-width" value={form.apiUrl} onChange={e => setForm(f => ({ ...f, apiUrl: e.target.value }))} placeholder="https://…" /></div>
              <div className="full-row"><span className="detail-label">Status URL (check payment)</span><input className="filter-select full-width" value={form.statusUrl} onChange={e => setForm(f => ({ ...f, statusUrl: e.target.value }))} placeholder="https://…" /></div>
              <div className="full-row"><span className="detail-label">Webhook URL (your backend)</span><input className="filter-select full-width" value={form.webhookUrl} onChange={e => setForm(f => ({ ...f, webhookUrl: e.target.value }))} placeholder="https://…" /></div>
              <div><span className="detail-label">Secret Key (if required)</span><input className="filter-select full-width" value={form.secretKey} onChange={e => setForm(f => ({ ...f, secretKey: e.target.value }))} placeholder="Optional" /></div>
              <div><span className="detail-label">User Token (if required)</span><input className="filter-select full-width" value={form.userToken} onChange={e => setForm(f => ({ ...f, userToken: e.target.value }))} placeholder="Optional" /></div>
              <div>
                <span className="detail-label">Status</span>
                <select className="filter-select full-width" value={String(form.enabled)} onChange={e => setForm(f => ({ ...f, enabled: e.target.value === 'true' }))}>
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </select>
              </div>
            </div>
            {(form.gatewayId === 'mobagateway' || editGw?.gatewayId === 'mobagateway') && (
              <div style={{ background: '#f3eeff', border: '1px solid #9b6ff733', borderRadius: 8, padding: '10px 14px', marginTop: 12, fontSize: 13 }}>
                <strong>💜 Moba Gateway defaults:</strong><br />
                API URL: <code>https://gateway.mobalegends.in/api/payments/create</code><br />
                Status URL: <code>https://gateway.mobalegends.in/api/payments/status</code><br />
                <button className="btn" style={{ marginTop: 8, fontSize: 12, height: 28 }}
                  onClick={() => setForm(f => ({ ...f, apiUrl: 'https://gateway.mobalegends.in/api/payments/create', statusUrl: 'https://gateway.mobalegends.in/api/payments/status' }))}>
                  Auto-fill Moba URLs
                </button>
              </div>
            )}
            {msg && <div className={`save-msg ${msg.startsWith('✅') ? 'msg-success' : 'msg-error'}`} style={{ marginTop: 12 }}>{msg}</div>}
            <div className="modal-actions" style={{ marginTop: 16 }}>
              <button className="btn" onClick={() => { setEditGw(null); setAddMode(false); }}>Cancel</button>
              <button className="btn btn-primary" onClick={saveGateway} disabled={saving}>{saving ? 'Saving…' : 'Save Gateway'}</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ── SMS PROVIDER ─────────────────────────────────────────────────────────────
const SmsProvider = () => {
  const [provider, setProvider]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [switching, setSwitching] = useState('');
  const [msg, setMsg]             = useState('');

  const PROVIDERS = {
    oneapi:   { label: 'OneAPI',   icon: '📡', color: '#3a8ef6', desc: 'Backend sends OTP SMS via OneAPI' },
    firebase: { label: 'Firebase', icon: '🔥', color: '#f5a623', desc: 'Frontend sends OTP via Firebase (backend skips SMS)' },
  };

  const fetchProvider = useCallback(async () => {
    setLoading(true);
    const data = await api('/admin/settings/sms-provider');
    setProvider(data?.smsProvider || 'oneapi');
    setLoading(false);
  }, []);

  useEffect(() => { fetchProvider(); }, [fetchProvider]);

  const switchProvider = async (p) => {
    if (p === provider) return;
    setSwitching(p); setMsg('');
    const data = await api('/admin/settings/sms-provider', {
      method: 'PATCH',
      body: JSON.stringify({ smsProvider: p }),
    });
    if (data?.success) {
      setProvider(p);
      setMsg(`✅ SMS provider switched to ${PROVIDERS[p]?.label}`);
      setTimeout(() => setMsg(''), 3000);
    } else {
      setMsg('❌ ' + (data?.message || 'Failed to update'));
    }
    setSwitching('');
  };

  return (
    <div className="section">
      {provider && !loading && (
        <div style={{ background: '#e8f5e9', border: '1.5px solid #34c89a', borderRadius: 10, padding: '12px 18px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20 }}>{PROVIDERS[provider]?.icon}</span>
          <div>
            <div style={{ fontWeight: 600, color: '#1a8a55' }}>Active SMS Provider: {PROVIDERS[provider]?.label}</div>
            <div style={{ fontSize: 12, color: '#555' }}>{PROVIDERS[provider]?.desc}</div>
          </div>
        </div>
      )}
      <div className="card">
        <div className="filters-row">
          <div className="card-title" style={{ fontSize: 15, fontWeight: 600 }}>SMS Provider</div>
          <button className="btn" onClick={fetchProvider}>↻ Refresh</button>
        </div>
        {loading ? <div className="center"><Spinner /></div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '12px 16px' }}>
            {Object.entries(PROVIDERS).map(([key, info]) => {
              const isActive = provider === key;
              return (
                <div key={key} style={{ border: isActive ? `2px solid ${info.color}` : '1px solid #e8e8e8', borderRadius: 10, padding: '16px 20px', background: isActive ? info.color + '11' : '#fff', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 200 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 10, background: info.color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{info.icon}</div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>{info.label}</div>
                      <div style={{ fontSize: 12, color: '#888' }}>{info.desc}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1 }}>
                    {isActive && <span style={{ background: info.color + '22', color: info.color, border: `1px solid ${info.color}55`, borderRadius: 99, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>✅ ACTIVE</span>}
                  </div>
                  {!isActive && (
                    <button className="btn btn-primary" style={{ fontSize: 12, height: 30, padding: '0 14px' }} onClick={() => switchProvider(key)} disabled={!!switching}>
                      {switching === key ? 'Switching…' : `⚡ Use ${info.label}`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {msg && <div className={`save-msg ${msg.startsWith('✅') ? 'msg-success' : 'msg-error'}`} style={{ margin: '0 16px 16px' }}>{msg}</div>}
      </div>
    </div>
  );
};

const calcAge = (dob) => {
  if (!dob) return '—';
  const d = new Date(dob);
  if (isNaN(d.getTime())) return '—';
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
};

const timeAgo = (date) => {
  if (!date) return '';
  const s = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  return `${Math.floor(s/86400)}d ago`;
};

const DOCUMENT_TYPE_LABELS = {
  aadhaar: 'Aadhaar',
  pan: 'PAN Card',
  passport: 'Passport',
  driving_license: 'Driving License',
  other: 'Other',
};

const REJECTION_REASONS = [
  'Document is blurry or unreadable',
  'Document appears tampered or edited',
  'Face does not match the ID photo',
  'Document has expired',
  'Wrong type of document uploaded',
  'Name on document does not match account',
  'Underage — not eligible',
  'Suspected fraudulent submission',
];

const KycStatusBadge = ({ status }) => {
  const map = {
    pending:          { bg: 'rgba(245,158,11,.15)', color: '#f5a623' },
    under_review:     { bg: 'rgba(234,179,8,.15)',  color: '#eab308' },
    approved:         { bg: 'rgba(34,197,94,.15)',   color: '#34c89a' },
    rejected:         { bg: 'rgba(239,68,68,.15)',   color: '#e05c5c' },
    guardian_required:{ bg: 'rgba(147,130,220,.15)', color: '#9382dc' },
    auto_approved:    { bg: 'rgba(34,197,94,.15)',   color: '#34c89a' },
  };
  const cfg = map[status] || { bg: 'rgba(170,170,170,.15)', color: '#aaa' };
  return (
    <span style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}55`, borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
};

const ZoomableImage = ({ src, alt }) => {
  const [zoomed, setZoomed] = useState(false);
  if (!src) return <div style={{ height: 300, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text-muted)', fontSize:13 }}>No image</div>;
  return (
    <>
      <div style={{ border:'1px solid var(--border)', borderRadius:10, height:300, display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', background:'var(--bg-elevated)', cursor:'zoom-in' }} onClick={() => setZoomed(true)}>
        <img src={src} alt={alt} style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain' }} />
      </div>
      {zoomed && (
        <div className="modal-overlay" onClick={() => setZoomed(false)} style={{ zIndex:1100, cursor:'zoom-out' }}>
          <img src={src} alt={alt} style={{ maxWidth:'90vw', maxHeight:'90vh', objectFit:'contain', borderRadius:10 }} onClick={e => e.stopPropagation()} />
        </div>
      )}
    </>
  );
};

const ScoreBar = ({ label, value, suffix = '%', thresholds }) => {
  const t = thresholds || { good: 70, warn: 40 };
  const color = value == null ? '#aaa' : value >= t.good ? '#34c89a' : value >= t.warn ? '#f5a623' : '#e05c5c';
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'var(--text-muted)', marginBottom:4 }}>
        <span>{label}</span>
        <span style={{ color, fontWeight:600 }}>{value == null ? 'N/A' : `${value.toFixed(1)}${suffix}`}</span>
      </div>
      <div style={{ height:8, borderRadius:4, background:'var(--bg-elevated)', overflow:'hidden' }}>
        <div style={{ width:`${Math.min(100, value || 0)}%`, height:'100%', background:color, borderRadius:4, transition:'width .3s' }} />
      </div>
    </div>
  );
};

const ReasonLabel = ({ reason }) => {
  const isError = reason.includes('fail') || reason.includes('low') || reason.includes('mismatch') || reason.includes('spoof') || reason === 'not_an_id' || reason === 'document_looks_like_selfie' || reason === 'revoked_by_admin';
  const isOk = reason.includes('pass') || reason.includes('match') || reason === 'auto_approved';
  const color = isError ? '#e05c5c' : isOk ? '#34c89a' : '#eab308';
  const LABELS = { not_an_id: '⚠ Not an ID document', document_looks_like_selfie: '⚠ Selfie uploaded as ID', revoked_by_admin: '🚫 Revoked by admin' };
  return (
    <span style={{ display:'inline-block', background:`${color}18`, color, border:`1px solid ${color}44`, borderRadius:6, padding:'2px 8px', fontSize:11, fontWeight:600, margin:'2px 4px 2px 0' }}>
      {LABELS[reason] || reason.replace(/_/g, ' ')}
    </span>
  );
};

const KycDetailModal = ({ kycId, onClose, onReviewed }) => {
  const [kyc, setKyc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejecting, setRejecting] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [revokeReason, setRevokeReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const isStaff = localStorage.getItem('admin_isStaff') === '1';

  useEffect(() => {
    if (!kycId) return;
    setLoading(true);
    api(`/admin/kyc/${kycId}`).then(data => {
      setKyc(data?.data || data?.kyc || data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [kycId]);

  const approve = async () => {
    setSaving(true); setMsg('');
    const data = await api(`/admin/kyc/${kyc._id}/approve`, { method: 'PATCH' });
    if (data?.success) { onReviewed(); onClose(); }
    else setMsg('Failed to approve: ' + (data?.error || data?.message || 'Unknown error'));
    setSaving(false);
  };

  const reject = async () => {
    if (!rejectionReason) { setMsg('Please select a rejection reason'); return; }
    setSaving(true); setMsg('');
    const data = await api(`/admin/kyc/${kyc._id}/reject`, {
      method: 'PATCH',
      body: JSON.stringify({ rejectionReason }),
    });
    if (data?.success) { onReviewed(); onClose(); }
    else setMsg('Failed to reject: ' + (data?.error || data?.message || 'Unknown error'));
    setSaving(false);
  };

  const revoke = async () => {
    if (!revokeReason.trim()) { setMsg('Please enter a reason for revoking'); return; }
    setSaving(true); setMsg('');
    const data = await api(`/admin/kyc/${kyc._id}/revoke`, {
      method: 'PATCH',
      body: JSON.stringify({ rejectionReason: revokeReason.trim() }),
    });
    if (data?.success) { onReviewed(); onClose(); }
    else setMsg('Failed to revoke: ' + (data?.error || data?.message || 'Unknown error'));
    setSaving(false);
  };

  if (loading) return <Modal title="KYC Submission" onClose={onClose}><div className="center"><Spinner /></div></Modal>;
  if (!kyc) return <Modal title="KYC Submission" onClose={onClose}><div style={{ textAlign:'center', padding:20, color:'var(--text-muted)' }}>Could not load KYC record.</div></Modal>;

  const bestLiveness = kyc.livenessScores && kyc.livenessScores.length > 0 ? Math.max(...kyc.livenessScores) : kyc.livenessConfidence;
  const possibleSpoof = bestLiveness != null && bestLiveness < 10;

  return (
    <Modal title="KYC Submission" onClose={onClose}>
      {/* Reviewer banner */}
      {(kyc.status === 'approved' || kyc.status === 'rejected') && (
        <div style={{ background: kyc.status === 'approved' ? 'rgba(34,197,94,.12)' : 'rgba(239,68,68,.12)', border: `1px solid ${kyc.status === 'approved' ? '#34c89a55' : '#e05c5c55'}`, borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 14, color: kyc.status === 'approved' ? '#34c89a' : '#e05c5c' }}>
          <strong>{kyc.status === 'approved' ? 'Approved' : 'Rejected'}</strong> by {kyc.reviewedByStaff?.username || kyc.reviewedBy?.name || kyc.reviewedBy?.email || 'Admin'}
          {kyc.reviewedAt && <span style={{ color:'var(--text-muted)', marginLeft:8 }}>{new Date(kyc.reviewedAt).toLocaleString('en-IN')}</span>}
          {kyc.status === 'rejected' && kyc.rejectionReason && (
            <div style={{ marginTop:6, color:'var(--text-primary)', fontSize:12 }}>Reason: {kyc.rejectionReason}</div>
          )}
        </div>
      )}

      {/* Spoof banner */}
      {possibleSpoof && (
        <div style={{ background:'rgba(239,68,68,.12)', border:'1px solid #e05c5c55', borderRadius:8, padding:'10px 14px', fontSize:13, marginBottom:14, color:'#e05c5c', fontWeight:600 }}>
          Possible spoof detected — best liveness score is below 10
        </div>
      )}

      {/* Side-by-side images */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
        <div>
          <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:6 }}>ID Document</div>
          <ZoomableImage src={kyc.documentImageUrl || kyc.documentImage} alt="ID document" />
        </div>
        <div>
          <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:6 }}>Liveness Photo</div>
          <ZoomableImage src={kyc.livenessReferenceImageUrl || kyc.livenessReferenceImage} alt="Liveness photo" />
        </div>
      </div>

      {/* Score bars */}
      <div style={{ marginBottom:16 }}>
        <ScoreBar label="Liveness Confidence" value={kyc.livenessConfidence} thresholds={{ good:80, warn:50 }} />
        {kyc.livenessScores && kyc.livenessScores.length > 1 && (
          <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:8 }}>
            All attempts ({kyc.livenessScores.length}): {kyc.livenessScores.map((s, i) => (
              <span key={i} style={{ fontWeight:600, marginRight:6 }}>{s.toFixed(1)}%</span>
            ))}
          </div>
        )}
        <ScoreBar label="Face Match Score" value={kyc.faceMatchScore} />
      </div>

      {/* Estimated age */}
      {(kyc.estimatedAgeLow != null || kyc.estimatedAgeHigh != null) && (
        <div style={{ fontSize:13, marginBottom:12, color:'var(--text-muted)' }}>
          Estimated age range: <strong style={{ color:'var(--text-primary)' }}>{kyc.estimatedAgeLow ?? '?'} – {kyc.estimatedAgeHigh ?? '?'}</strong>
        </div>
      )}

      {/* DOB comparison */}
      <div style={{ border:'1px solid var(--border)', borderRadius:10, padding:'12px 14px', marginBottom:16, background:'var(--bg-elevated)' }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, fontSize:13 }}>
          <div>
            <div style={{ color:'var(--text-muted)', fontSize:12 }}>User Entered DOB</div>
            <div style={{ fontWeight:600 }}>{kyc.dateOfBirth ? new Date(kyc.dateOfBirth).toLocaleDateString('en-IN') : '—'}</div>
          </div>
          <div>
            <div style={{ color:'var(--text-muted)', fontSize:12 }}>Extracted DOB</div>
            <div style={{ fontWeight:600 }}>{kyc.extractedDob ? new Date(kyc.extractedDob).toLocaleDateString('en-IN') : 'Could not extract'}</div>
          </div>
        </div>
        <div style={{ marginTop:10 }}>
          <span style={{ color:'var(--text-muted)', fontSize:12, marginRight:6 }}>DOB Match:</span>
          {kyc.dobVerified
            ? <span style={{ color:'#34c89a', fontWeight:600 }}>Match</span>
            : <span style={{ color:'#e05c5c', fontWeight:600 }}>{kyc.extractedDob ? 'Mismatch' : 'Not verified'}</span>
          }
        </div>
      </div>

      {/* Decision reasons */}
      {kyc.decisionReasons && kyc.decisionReasons.length > 0 && (
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:6 }}>Decision Reasons</div>
          <div style={{ display:'flex', flexWrap:'wrap' }}>
            {kyc.decisionReasons.map((r, i) => <ReasonLabel key={i} reason={r} />)}
          </div>
        </div>
      )}

      {/* User info grid */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, fontSize:13, marginBottom:16 }}>
        <div><span style={{ color:'var(--text-muted)' }}>Name:</span> {kyc.userId?.name || '—'}</div>
        <div><span style={{ color:'var(--text-muted)' }}>Email:</span> {kyc.userId?.email || '—'}</div>
        <div><span style={{ color:'var(--text-muted)' }}>Phone:</span> {kyc.userId?.phone || '—'}</div>
        <div><span style={{ color:'var(--text-muted)' }}>Document:</span> {DOCUMENT_TYPE_LABELS[kyc.documentType] || kyc.documentType}</div>
        <div><span style={{ color:'var(--text-muted)' }}>KYC Type:</span> <span style={{ textTransform:'capitalize' }}>{kyc.kycType || 'self'}</span></div>
        <div><span style={{ color:'var(--text-muted)' }}>Age:</span> {calcAge(kyc.dateOfBirth)}</div>
        {kyc.extractedName && <div><span style={{ color:'var(--text-muted)' }}>Extracted Name:</span> {kyc.extractedName}</div>}
      </div>

      {/* Auto-approved badge */}
      {kyc.autoApproved && (
        <div style={{ marginBottom:14 }}>
          <span style={{ display:'inline-block', background:'rgba(34,197,94,.15)', color:'#34c89a', border:'1px solid #34c89a55', borderRadius:6, padding:'3px 10px', fontSize:11, fontWeight:600 }}>
            Auto-approved
          </span>
        </div>
      )}

      {/* Guardian info */}
      {kyc.kycType === 'guardian' && (
        <div style={{ border:'1px solid var(--border)', borderRadius:10, padding:'12px 14px', marginBottom:16, background:'var(--bg-elevated)' }}>
          <div style={{ fontSize:13, fontWeight:600, marginBottom:8 }}>Guardian Info</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, fontSize:13 }}>
            {kyc.guardianRelationship && <div><span style={{ color:'var(--text-muted)' }}>Relationship:</span> <span style={{ textTransform:'capitalize' }}>{kyc.guardianRelationship}</span></div>}
            {kyc.guardianConsent?.consentedAt && <div><span style={{ color:'var(--text-muted)' }}>Consent time:</span> {new Date(kyc.guardianConsent.consentedAt).toLocaleString('en-IN')}</div>}
            {kyc.childUser && <div><span style={{ color:'var(--text-muted)' }}>Child:</span> {kyc.childUser.email ? kyc.childUser.email.replace(/(.{2})(.*)(@)/, '$1***$3') : '—'}</div>}
          </div>
        </div>
      )}

      {/* Messages */}
      {msg && <div style={{ padding:'8px 12px', borderRadius:8, fontSize:13, marginBottom:12, background:'rgba(239,68,68,.12)', color:'#e05c5c', border:'1px solid #e05c5c44' }}>{msg}</div>}

      {/* Revoke action for approved records (admin only) */}
      {kyc.status === 'approved' && !isStaff && (
        revoking ? (
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:8 }}>Reason for revoking approval:</div>
            <textarea value={revokeReason} onChange={e => setRevokeReason(e.target.value)} rows={2} placeholder="Enter reason..."
              style={{ width:'100%', padding:8, borderRadius:8, border:'1px solid var(--border)', background:'var(--bg-page)', color:'var(--text-primary)', fontSize:13, resize:'vertical' }} />
            <div style={{ display:'flex', gap:8, marginTop:8 }}>
              <button className="btn" onClick={() => { setRevoking(false); setRevokeReason(''); }} disabled={saving}>Cancel</button>
              <button className="btn" style={{ background:'#e05c5c', color:'#fff', borderColor:'#e05c5c' }} onClick={revoke} disabled={saving}>
                {saving ? 'Revoking...' : 'Confirm Revoke'}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ marginBottom:12 }}>
            <button className="btn" style={{ background:'#e05c5c', color:'#fff', borderColor:'#e05c5c' }} onClick={() => setRevoking(true)} disabled={saving}>
              Revoke Approval
            </button>
          </div>
        )
      )}

      {/* Pending info — user hasn't done face scan yet */}
      {kyc.status === 'pending' && (
        <div style={{ padding:'10px 14px', borderRadius:8, fontSize:13, marginBottom:12, background:'rgba(234,179,8,.1)', color:'#eab308', border:'1px solid #eab30844' }}>
          ⏳ Waiting for user's face scan — this record cannot be approved or rejected yet.
        </div>
      )}

      {/* Actions */}
      {kyc.status !== 'approved' && kyc.status !== 'rejected' && kyc.status !== 'pending' && (
        rejecting ? (
          <div>
            <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:8 }}>Select rejection reason:</div>
            {REJECTION_REASONS.map(r => (
              <label key={r} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 0', fontSize:13, cursor:'pointer' }}>
                <input type="radio" name="rejReason" value={r} checked={rejectionReason === r} onChange={() => setRejectionReason(r)} />
                {r}
              </label>
            ))}
            <div style={{ display:'flex', gap:8, marginTop:12 }}>
              <button className="btn" onClick={() => { setRejecting(false); setRejectionReason(''); }} disabled={saving}>Cancel</button>
              <button className="btn" style={{ background:'#e05c5c', color:'#fff', borderColor:'#e05c5c' }} onClick={reject} disabled={saving}>
                {saving ? 'Rejecting...' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display:'flex', gap:10 }}>
            <button className="btn" style={{ background:'#34c89a', color:'#fff', borderColor:'#34c89a', flex:1 }} onClick={approve} disabled={saving}>
              {saving ? 'Approving...' : 'Approve'}
            </button>
            <button className="btn" style={{ background:'#e05c5c', color:'#fff', borderColor:'#e05c5c', flex:1 }} onClick={() => setRejecting(true)} disabled={saving}>
              Reject
            </button>
          </div>
        )
      )}
    </Modal>
  );
};

const KycReviews = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('queue');
  const [viewKycId, setViewKycId] = useState(null);
  const isStaffUser = localStorage.getItem('admin_isStaff') === '1';

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    const statusParam = statusFilter === 'queue' ? 'under_review' : statusFilter;
    const data = await api(`/admin/kyc/pending?status=${statusParam}`);
    setEntries(data?.entries || data?.data || []);
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const statusTabs = [
    { value: 'queue', label: 'Queue' },
    { value: 'pending', label: 'In progress' },
    { value: 'under_review', label: 'Under review' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
  ];

  return (
    <div className="section">
      <div className="card">
        <div className="filters-row" style={{ marginBottom:12 }}>
          <div className="card-title" style={{ fontSize:15, fontWeight:600 }}>KYC Reviews ({entries.length})</div>
          <button className="btn" onClick={fetchEntries}>Refresh</button>
        </div>
        {!isStaffUser && (
          <div style={{ display:'flex', gap:6, marginBottom:14, flexWrap:'wrap' }}>
            {statusTabs.map(t => (
              <button key={t.value} className={`tab-btn ${statusFilter === t.value ? 'tab-active' : ''}`}
                onClick={() => setStatusFilter(t.value)}>
                {t.label}
              </button>
            ))}
          </div>
        )}
        {loading ? <div className="center"><Spinner /></div> : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>User</th><th>Type</th><th>Document</th>
                  <th>Decision</th><th>Status</th><th>Time</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0
                  ? <tr><td colSpan={7} className="empty">No KYC submissions found</td></tr>
                  : entries.map((k) => {
                    const bestLiveness = k.livenessScores && k.livenessScores.length > 0 ? Math.max(...k.livenessScores) : k.livenessConfidence;
                    const spoof = bestLiveness != null && bestLiveness < 10;
                    return (
                      <tr key={k._id}>
                        <td>
                          <span className="customer-name">{k.userId?.name || '—'}</span>
                          <div style={{ fontSize:11, color:'var(--text-muted)' }}>{k.userId?.email || ''}</div>
                        </td>
                        <td>
                          <span style={{ background: k.kycType === 'guardian' ? 'rgba(147,130,220,.15)' : 'rgba(58,142,246,.15)', color: k.kycType === 'guardian' ? '#9382dc' : '#3a8ef6', border: `1px solid ${k.kycType === 'guardian' ? '#9382dc55' : '#3a8ef655'}`, borderRadius:6, padding:'2px 8px', fontSize:11, fontWeight:600, textTransform:'capitalize' }}>
                            {k.kycType || 'self'}
                          </span>
                        </td>
                        <td><span className="pill-outline">{DOCUMENT_TYPE_LABELS[k.documentType] || k.documentType}</span></td>
                        <td>
                          {k.decisionReasons && k.decisionReasons.length > 0 && (
                            <ReasonLabel reason={k.decisionReasons[0]} />
                          )}
                          {spoof && <span style={{ display:'inline-block', background:'rgba(239,68,68,.15)', color:'#e05c5c', border:'1px solid #e05c5c44', borderRadius:6, padding:'2px 8px', fontSize:11, fontWeight:600, marginLeft:4 }}>SPOOF</span>}
                        </td>
                        <td>
                          <KycStatusBadge status={k.status} />
                          {k.status === 'pending' && <div style={{ fontSize:10, color:'var(--text-muted)', marginTop:2 }}>Waiting for user's face scan</div>}
                        </td>
                        <td style={{ fontSize:12, color:'var(--text-muted)' }}>{timeAgo(k.createdAt)}</td>
                        <td><button className="btn" onClick={() => setViewKycId(k._id)}>Review</button></td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {viewKycId && (
        <KycDetailModal kycId={viewKycId} onClose={() => setViewKycId(null)} onReviewed={fetchEntries} />
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// STAFF ACCOUNTS
// ════════════════════════════════════════════════════════════════════════════════
const StaffAccounts = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ username: '', password: '', role: 'kyc_reviewer' });
  const [resetId, setResetId] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetMsg, setResetMsg] = useState('');
  const [toggling, setToggling] = useState('');

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api('/admin/staff');
      setStaff(data?.staff || data?.data || []);
    } catch { setStaff([]); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const createStaff = async () => {
    if (!createForm.username.trim()) { setMsg('Username is required'); return; }
    if (!createForm.password || createForm.password.length < 12) { setMsg('Password must be at least 12 characters'); return; }
    setCreating(true); setMsg('');
    try {
      const data = await api('/admin/staff', {
        method: 'POST',
        body: JSON.stringify(createForm),
      });
      if (data?.success || data?.data) {
        setMsg('Staff account created');
        setCreateForm({ username: '', password: '', role: 'kyc_reviewer' });
        fetchStaff();
      } else {
        setMsg('Error: ' + (data?.message || data?.error || 'Failed'));
      }
    } catch { setMsg('Error: Network error'); }
    setCreating(false);
    setTimeout(() => setMsg(''), 4000);
  };

  const doResetPassword = async () => {
    if (!resetPassword || resetPassword.length < 12) { setResetMsg('Password must be at least 12 characters'); return; }
    setResetMsg('');
    try {
      const data = await api(`/admin/staff/${resetId}/reset-password`, {
        method: 'PATCH',
        body: JSON.stringify({ password: resetPassword }),
      });
      if (data?.success || data?.data) {
        setResetMsg('Password reset successfully');
        setResetPassword('');
        setTimeout(() => { setResetId(null); setResetMsg(''); }, 1500);
      } else {
        setResetMsg('Error: ' + (data?.message || data?.error || 'Failed'));
      }
    } catch { setResetMsg('Error: Network error'); }
  };

  const toggleActive = async (s) => {
    setToggling(s._id);
    try {
      const data = await api(`/admin/staff/${s._id}/toggle-active`, { method: 'PATCH' });
      if (data?.success || data?.data) fetchStaff();
    } catch {}
    setToggling('');
  };

  return (
    <div className="section">
      <div className="card">
        <div className="filters-row">
          <div className="card-title" style={{ fontSize:15, fontWeight:600 }}>Create Staff Account</div>
        </div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', padding:'0 16px 16px' }}>
          <input className="filter-select" style={{ flex:'1 1 160px' }} placeholder="Username"
            value={createForm.username} onChange={e => setCreateForm(f => ({ ...f, username: e.target.value }))} />
          <input className="filter-select" style={{ flex:'1 1 200px' }} type="password" placeholder="Password (min 12 chars)"
            value={createForm.password} onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))} />
          <select className="filter-select" value={createForm.role} onChange={e => setCreateForm(f => ({ ...f, role: e.target.value }))}>
            <option value="kyc_reviewer">KYC Reviewer</option>
          </select>
          <button className="btn btn-primary" onClick={createStaff} disabled={creating}>
            {creating ? 'Creating…' : '+ Create'}
          </button>
        </div>
        {msg && <div className={`save-msg ${msg.startsWith('Error') ? 'msg-error' : 'msg-success'}`} style={{ margin:'0 16px 16px' }}>{msg}</div>}
      </div>

      <div className="card" style={{ marginTop:12 }}>
        <div className="filters-row">
          <div className="card-title" style={{ fontSize:15, fontWeight:600 }}>Staff Accounts ({staff.length})</div>
          <button className="btn" onClick={fetchStaff}>↻ Refresh</button>
        </div>
        {loading ? <div className="center"><Spinner /></div> : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Username</th><th>Role</th><th>Status</th><th>Last Login</th><th>Created By</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {staff.length === 0
                  ? <tr><td colSpan={6} className="empty">No staff accounts</td></tr>
                  : staff.map(s => (
                    <tr key={s._id}>
                      <td><span className="customer-name">{s.username}</span></td>
                      <td><span className="pill-outline">{s.role}</span></td>
                      <td>
                        <span className={`badge ${s.active ? 'badge-green' : 'badge-red'}`}>
                          <span className="dot" style={{ background: s.active ? '#34c89a' : '#e05c5c' }} />
                          {s.active ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td style={{ fontSize:12, color:'var(--text-muted)' }}>{s.lastLoginAt ? timeAgo(s.lastLoginAt) : 'Never'}</td>
                      <td style={{ fontSize:12, color:'var(--text-muted)' }}>{s.createdBy?.username || s.createdBy?.email || s.createdBy?.name || '—'}</td>
                      <td style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                        <button className="btn-edit" onClick={() => { setResetId(s._id); setResetPassword(''); setResetMsg(''); }}>Reset Password</button>
                        <button className="btn-edit" style={{ color: s.active ? '#e05c5c' : '#34c89a' }}
                          onClick={() => toggleActive(s)} disabled={toggling === s._id}>
                          {toggling === s._id ? '…' : (s.active ? 'Disable' : 'Enable')}
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {resetId && (
        <Modal title="Reset Password" onClose={() => setResetId(null)}>
          <div className="order-detail">
            <div className="form-group">
              <label>New Password (min 12 characters)</label>
              <input type="password" className="filter-select full-width" placeholder="Enter new password…"
                value={resetPassword} onChange={e => setResetPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && doResetPassword()} />
            </div>
            {resetMsg && <div className={`save-msg ${resetMsg.startsWith('Error') ? 'msg-error' : 'msg-success'}`} style={{ marginBottom:12 }}>{resetMsg}</div>}
            <div className="modal-actions">
              <button className="btn" onClick={() => setResetId(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={doResetPassword}>Reset Password</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

const Blacklist = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [reason, setReason] = useState('');
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState('');
  const [msg, setMsg] = useState('');

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    const data = await api('/admin/blacklist');
    setEntries(data?.entries || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const addUsername = async () => {
    if (!username.trim()) return;
    setAdding(true); setMsg('');
    const data = await api('/admin/blacklist', {
      method: 'POST',
      body: JSON.stringify({ username: username.trim(), reason: reason.trim() || undefined }),
    });
    if (data?.success) {
      setUsername(''); setReason('');
      setMsg('✅ Username blacklisted');
      fetchEntries();
    } else {
      setMsg('❌ ' + (data?.error || data?.message || 'Failed to add'));
    }
    setAdding(false);
    setTimeout(() => setMsg(''), 3000);
  };

  const removeUsername = async (name) => {
    if (!window.confirm(`Remove "${name}" from the blacklist?`)) return;
    setRemoving(name);
    const data = await api(`/admin/blacklist/${encodeURIComponent(name)}`, { method: 'DELETE' });
    if (data?.success) fetchEntries();
    else setMsg('❌ ' + (data?.error || data?.message || 'Failed to remove'));
    setRemoving('');
  };

  return (
    <div className="section">
      <div className="card">
        <div className="filters-row">
          <div className="card-title" style={{ fontSize: 15, fontWeight: 600 }}>Blacklisted Roblox Usernames</div>
          <button className="btn" onClick={fetchEntries}>↻ Refresh</button>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '12px 16px' }}>
          <input
            className="filter-select"
            style={{ flex: '1 1 200px' }}
            placeholder="Roblox username or player ID"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addUsername()}
          />
          <input
            className="filter-select"
            style={{ flex: '1 1 200px' }}
            placeholder="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addUsername()}
          />
          <button className="btn btn-primary" onClick={addUsername} disabled={adding || !username.trim()}>
            {adding ? 'Adding…' : '+ Add to Blacklist'}
          </button>
        </div>

        {msg && <div className={`save-msg ${msg.startsWith('✅') ? 'msg-success' : 'msg-error'}`} style={{ margin: '0 16px 16px' }}>{msg}</div>}

        {loading ? (
          <div className="center"><Spinner /></div>
        ) : entries.length === 0 ? (
          <div style={{ padding: '24px 16px', color: '#888', textAlign: 'center' }}>No usernames blacklisted.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 16px 16px' }}>
            {entries.map((entry) => (
              <div key={entry._id} style={{ display: 'flex', alignItems: 'center', gap: 16, border: '1px solid #e8e8e8', borderRadius: 10, padding: '12px 16px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <div style={{ fontWeight: 600 }}>{entry.username}</div>
                  {entry.reason && <div style={{ fontSize: 12, color: '#888' }}>{entry.reason}</div>}
                </div>
                <div style={{ fontSize: 12, color: '#888' }}>
                  {entry.blockedBy?.name ? `Blocked by ${entry.blockedBy.name}` : ''}
                  {entry.createdAt ? ` · ${new Date(entry.createdAt).toLocaleDateString('en-IN')}` : ''}
                </div>
                <button
                  className="btn"
                  style={{ color: '#e05c5c', borderColor: '#e05c5c55' }}
                  onClick={() => removeUsername(entry.username)}
                  disabled={removing === entry.username}
                >
                  {removing === entry.username ? 'Removing…' : '✕ Remove'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── ROBLOX PLAYER BADGE (shared) ─────────────────────────────────────────────
const RobloxPlayerBadge = ({ player, size = 'normal' }) => {
  if (!player) return <span style={{ color: '#aaa', fontSize: 12 }}>No Roblox info</span>;
  const isSmall = size === 'small';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: isSmall ? 8 : 10, padding: isSmall ? '4px 0' : '6px 0' }}>
      {player.avatarUrl && (
        <img src={player.avatarUrl} alt="" style={{ width: isSmall ? 32 : 40, height: isSmall ? 32 : 40, borderRadius: '50%', border: '2px solid #34c89a' }} />
      )}
      <div>
        <div style={{ fontWeight: 700, fontSize: isSmall ? 13 : 14, color: '#fff' }}>@{player.username}</div>
        <div style={{ fontSize: isSmall ? 11 : 12, color: '#aaa' }}>{player.displayName} · ID {player.userId}</div>
      </div>
    </div>
  );
};



// ── CHAT ORDERS (Admin) ──────────────────────────────────────────────────────
const ChatOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [detail, setDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [agents, setAgents] = useState([]);
  const [agentTab, setAgentTab] = useState(false);
  const [createForm, setCreateForm] = useState({ username: '', password: '', displayName: '' });
  const [resetForm, setResetForm] = useState({ agentId: null, password: '' });
  const [agentMsg, setAgentMsg] = useState(null);
  const [editingAgent, setEditingAgent] = useState(null);
  const [editDisplayName, setEditDisplayName] = useState('');

  const handleUpdateDisplayName = async (agentId) => {
    if (!editDisplayName.trim()) return;
    try {
      const data = await api(`/admin/chat/agents/${agentId}/display-name`, { method: 'PATCH', body: JSON.stringify({ displayName: editDisplayName.trim() }) });
      if (data.success) { setAgentMsg('Display name updated'); setEditingAgent(null); fetchAgents(); }
      else setAgentMsg(data.error || 'Failed');
    } catch { setAgentMsg('Network error'); }
  };

  const handleRemoveAvatar = async (agentId) => {
    if (!window.confirm('Remove this agent\'s avatar?')) return;
    try {
      const data = await api(`/admin/chat/agents/${agentId}/avatar`, { method: 'DELETE' });
      if (data.success) { setAgentMsg('Avatar removed'); fetchAgents(); }
      else setAgentMsg(data.error || 'Failed');
    } catch { setAgentMsg('Network error'); }
  };

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (statusFilter) params.set('status', statusFilter);
      const data = await api(`/admin/chat/orders?${params}`);
      setOrders(data.orders || []);
      setTotal(data.total || 0);
    } catch { setOrders([]); }
    setLoading(false);
  }, [page, statusFilter]);

  const fetchAgents = useCallback(async () => {
    try {
      const data = await api('/admin/chat/agents');
      setAgents(data.agents || []);
    } catch { setAgents([]); }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  useEffect(() => { if (agentTab) fetchAgents(); }, [agentTab, fetchAgents]);

  const viewDetail = async (co) => {
    try {
      const data = await api(`/admin/chat/orders/${co._id}`);
      setDetail(data.chatOrder);
      setMessages(data.messages || []);
    } catch {}
  };

  const handleSuspend = async (agentId, suspend) => {
    try {
      await api(`/admin/chat/agents/${agentId}/suspend`, { method: 'POST', body: JSON.stringify({ suspended: suspend }) });
      fetchAgents();
    } catch {}
  };

  const handleCreateAgent = async () => {
    if (!createForm.username || !createForm.password) return;
    if (createForm.password.length < 12) { setAgentMsg('Password must be at least 12 characters'); return; }
    try {
      const data = await api('/admin/chat/agents/create', { method: 'POST', body: JSON.stringify({ username: createForm.username, password: createForm.password, displayName: createForm.displayName || undefined }) });
      if (data.success) { setAgentMsg('Agent created'); setCreateForm({ username: '', password: '', displayName: '' }); fetchAgents(); }
      else setAgentMsg(data.error || 'Failed');
    } catch { setAgentMsg('Network error'); }
  };

  const handleResetPassword = async () => {
    if (!resetForm.agentId || !resetForm.password) return;
    if (resetForm.password.length < 12) { setAgentMsg('Password must be at least 12 characters'); return; }
    try {
      const data = await api(`/admin/chat/agents/${resetForm.agentId}/reset-password`, { method: 'POST', body: JSON.stringify({ password: resetForm.password }) });
      if (data.success) { setAgentMsg('Password reset'); setResetForm({ agentId: null, password: '' }); }
      else setAgentMsg(data.error || 'Failed');
    } catch { setAgentMsg('Network error'); }
  };

  const handleAdminRefund = async (chatOrderId) => {
    const reason = prompt('Reason for refund (required, at least 5 characters):');
    if (!reason || reason.trim().length < 5) { setAgentMsg('Refund reason must be at least 5 characters'); return; }
    try {
      const data = await api(`/admin/chat/orders/${chatOrderId}/refund`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason.trim() }),
      });
      if (data.success) {
        setAgentMsg(`Refunded ₹${data.amount} to buyer wallet`);
        fetchOrders();
        setDetail(null);
        setMessages([]);
      }
    } catch (err) { setAgentMsg(err.message || 'Refund failed'); }
  };

  const STATUSES = ['', 'waiting_for_agent', 'in_progress', 'delivered', 'confirmed', 'auto_confirmed', 'paused', 'awaiting_buyer', 'abandoned', 'cancelled'];

  return (
    <div>
      <div className="toolbar">
        <button className={`tab-btn ${!agentTab ? 'tab-active' : ''}`} onClick={() => setAgentTab(false)}>💬 Chat Orders ({total})</button>
        <button className={`tab-btn ${agentTab ? 'tab-active' : ''}`} onClick={() => setAgentTab(true)}>👷 Agents</button>
      </div>

      {!agentTab ? (
        <>
          <div className="toolbar" style={{ marginTop: 12 }}>
            <select className="filter-select" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
              {STATUSES.map(s => <option key={s} value={s}>{s || 'All statuses'}</option>)}
            </select>
            <button className="btn" onClick={fetchOrders}>↻ Refresh</button>
          </div>

          {loading ? <Spinner /> : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr>
                  <th>Order</th><th>Buyer</th><th>Agent</th><th>Status</th><th>Created</th><th></th>
                </tr></thead>
                <tbody>
                  {orders.length === 0 ? <tr><td colSpan={6} style={{ textAlign:'center', padding:20 }}>No chat orders</td></tr> :
                    orders.map(co => (
                      <tr key={co._id}>
                        <td style={{ fontSize:12 }}>{co.orderId?.orderId ? formatShortOrderId(co.orderId.orderId) : co.orderId?._id?.slice(-8) || '—'}</td>
                        <td>{co.buyerId?.username || '—'}</td>
                        <td>{co.agentId?.username || <em style={{ color:'#aaa' }}>unassigned</em>}</td>
                        <td><StatusBadge status={co.status} /></td>
                        <td style={{ fontSize:12 }}>{new Date(co.createdAt).toLocaleString()}</td>
                        <td><button className="btn btn-sm" onClick={() => viewDetail(co)}>View</button></td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          )}

          {total > 20 && (
            <div className="pagination" style={{ marginTop: 12 }}>
              <button className="btn btn-sm" disabled={page <= 1} onClick={() => setPage(p => p-1)}>← Prev</button>
              <span style={{ margin:'0 12px', fontSize:13 }}>Page {page} of {Math.ceil(total/20)}</span>
              <button className="btn btn-sm" disabled={page >= Math.ceil(total/20)} onClick={() => setPage(p => p+1)}>Next →</button>
            </div>
          )}
        </>
      ) : (
        <>
          {agentMsg && <div style={{ margin:'12px 0 0', padding:'8px 12px', borderRadius:6, background:'#1a2a3a', border:'1px solid #2a4a6a', color:'#5ba3f5', fontSize:13 }}>{agentMsg}</div>}

          <div style={{ display:'flex', gap:8, marginTop:12, flexWrap:'wrap', alignItems:'flex-end' }}>
            <div>
              <label style={{ fontSize:11, color:'#aaa', display:'block', marginBottom:2 }}>New Agent Username</label>
              <input className="filter-select" value={createForm.username} onChange={e => setCreateForm(f => ({ ...f, username: e.target.value }))} placeholder="username" style={{ width:140 }} />
            </div>
            <div>
              <label style={{ fontSize:11, color:'#aaa', display:'block', marginBottom:2 }}>Display Name</label>
              <input className="filter-select" value={createForm.displayName || ''} onChange={e => setCreateForm(f => ({ ...f, displayName: e.target.value }))} placeholder="display name" style={{ width:140 }} />
            </div>
            <div>
              <label style={{ fontSize:11, color:'#aaa', display:'block', marginBottom:2 }}>Password (min 12)</label>
              <input className="filter-select" type="password" value={createForm.password} onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))} placeholder="password" style={{ width:160 }} />
            </div>
            <button className="btn btn-primary" onClick={handleCreateAgent}>+ Create Agent</button>
          </div>

          <div style={{ display:'flex', gap:8, marginTop:8, flexWrap:'wrap', alignItems:'flex-end' }}>
            <div>
              <label style={{ fontSize:11, color:'#aaa', display:'block', marginBottom:2 }}>Reset Password For</label>
              <select className="filter-select" value={resetForm.agentId || ''} onChange={e => setResetForm(f => ({ ...f, agentId: e.target.value || null }))} style={{ width:160 }}>
                <option value="">Select agent</option>
                {agents.map(a => <option key={a._id} value={a._id}>{a.username}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize:11, color:'#aaa', display:'block', marginBottom:2 }}>New Password (min 12)</label>
              <input className="filter-select" type="password" value={resetForm.password} onChange={e => setResetForm(f => ({ ...f, password: e.target.value }))} placeholder="new password" style={{ width:180 }} />
            </div>
            <button className="btn" onClick={handleResetPassword}>Reset Password</button>
          </div>

          <button className="btn" style={{ marginTop:12 }} onClick={fetchAgents}>↻ Refresh Agents</button>
          <div className="table-wrap" style={{ marginTop:12 }}>
            <table className="table">
              <thead><tr>
                <th></th><th>Username</th><th>Display Name</th><th>Online</th><th>Active</th><th>Completed</th><th>Suspended</th><th></th>
              </tr></thead>
              <tbody>
                {agents.length === 0 ? <tr><td colSpan={8} style={{ textAlign:'center', padding:20 }}>No delivery agents</td></tr> :
                  agents.map(a => (
                    <tr key={a._id}>
                      <td>
                        <div style={{ width:32, height:32, borderRadius:6, overflow:'hidden', background:'#d29922', display:'flex', alignItems:'center', justifyContent:'center' }}>
                          {a.avatarUrl ? <img src={a.avatarUrl} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                            : <span style={{ color:'#000', fontWeight:700, fontSize:14 }}>{(a.displayName || a.username || '?').charAt(0).toUpperCase()}</span>}
                        </div>
                      </td>
                      <td style={{ fontSize:12 }}>{a.username || a._id?.slice(-8)}</td>
                      <td>
                        {editingAgent === a._id ? (
                          <div style={{ display:'flex', gap:4 }}>
                            <input className="filter-select" value={editDisplayName} onChange={e => setEditDisplayName(e.target.value)} style={{ width:100, fontSize:12 }}
                              onKeyDown={e => e.key === 'Enter' && handleUpdateDisplayName(a._id)} />
                            <button className="btn btn-sm" onClick={() => handleUpdateDisplayName(a._id)}>✓</button>
                            <button className="btn btn-sm" onClick={() => setEditingAgent(null)}>✕</button>
                          </div>
                        ) : (
                          <span style={{ cursor:'pointer' }} onClick={() => { setEditingAgent(a._id); setEditDisplayName(a.displayName || ''); }}>
                            {a.displayName || <em style={{ color:'#aaa' }}>not set</em>} ✎
                          </span>
                        )}
                      </td>
                      <td>{a.online ? <span style={{ color:'#34c89a' }}>● Yes</span> : <span style={{ color:'#aaa' }}>○ No</span>}</td>
                      <td>{a.activeCount ?? 0}</td>
                      <td>{a.deliveredCount ?? 0}</td>
                      <td>{a.suspended ? <span style={{ color:'#e05c5c' }}>Yes</span> : 'No'}</td>
                      <td style={{ display:'flex', gap:4 }}>
                        <button className="btn btn-sm" onClick={() => handleSuspend(a.agentId || a._id, !a.suspended)}>
                          {a.suspended ? 'Unsuspend' : 'Suspend'}
                        </button>
                        {a.avatarUrl && <button className="btn btn-sm" style={{ color:'#e05c5c' }} onClick={() => handleRemoveAvatar(a._id)}>Remove Pic</button>}
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </>
      )}

      {detail && (
        <Modal title="Chat Order Detail" onClose={() => { setDetail(null); setMessages([]); }}>
          <div className="detail-grid">
            <div><span className="detail-label">Status</span><StatusBadge status={detail.status} /></div>
            <div><span className="detail-label">Buyer</span><span>{detail.buyerId?.username || '—'}</span></div>
            <div><span className="detail-label">Agent</span><span>{detail.agentId?.username || 'Unassigned'}</span></div>
            {detail.robloxPlayer && (
              <div className="full-row"><span className="detail-label">Roblox Player</span><RobloxPlayerBadge player={detail.robloxPlayer} /></div>
            )}
            <div><span className="detail-label">Created</span><span style={{ fontSize:12 }}>{new Date(detail.createdAt).toLocaleString()}</span></div>
            {detail.proofImage && (
              <div className="full-row">
                <span className="detail-label">Proof Image</span>
                <img src={detail.proofImage} alt="Proof" style={{ maxWidth:300, borderRadius:8, marginTop:4 }} />
              </div>
            )}
          </div>
          {detail.items && detail.items.length > 0 && (
            <>
              <h4 style={{ margin:'16px 0 8px', fontSize:14 }}>Items ({detail.items.filter(i => i.fulfilled).length}/{detail.items.length} fulfilled)</h4>
              <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                {detail.items.map(item => (
                  <div key={item.orderItemId} style={{ display:'flex', alignItems:'center', gap:8, padding:'4px 8px', borderRadius:4, background: item.fulfilled ? 'rgba(52,200,154,0.08)' : 'transparent' }}>
                    <span style={{ color: item.fulfilled ? '#34c89a' : '#aaa' }}>{item.fulfilled ? '✓' : '○'}</span>
                    <span style={{ fontSize:13, color: item.fulfilled ? '#aaa' : '#eee' }}>{item.name}{item.quantity > 1 ? ` ×${item.quantity}` : ''}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {!['confirmed', 'auto_confirmed', 'cancelled'].includes(detail.status) && (
            <div style={{ marginTop:12 }}>
              <button className="btn btn-sm" style={{ background:'#e05c5c', color:'#fff' }} onClick={() => handleAdminRefund(detail._id)}>
                Refund to Wallet
              </button>
            </div>
          )}

          <h4 style={{ margin:'16px 0 8px', fontSize:14 }}>Messages ({messages.length})</h4>
          <div style={{ maxHeight:300, overflowY:'auto', background:'var(--bg-card)', borderRadius:8, padding:12 }}>
            {messages.length === 0 ? <div style={{ color:'#aaa', textAlign:'center' }}>No messages</div> :
              messages.map(m => (
                <div key={m._id} style={{ marginBottom:8, padding:'6px 10px', borderRadius:6, background: m.senderType==='buyer' ? '#1a2a3a' : m.senderType==='agent' ? '#1a3a1a' : '#2a2a2a' }}>
                  <div style={{ fontSize:11, fontWeight:600, color: m.senderType==='buyer' ? '#5ba3f5' : m.senderType==='agent' ? '#34c89a' : '#aaa' }}>
                    {m.senderType} · {new Date(m.createdAt).toLocaleTimeString()}
                    {m.visibility === 'staff' && <span style={{ marginLeft:6, fontSize:9, color:'#d29922', textTransform:'uppercase' }}>staff only</span>}
                  </div>
                  <div style={{ fontSize:13, color:'#eee', marginTop:2 }}>{m.filtered ? <em style={{ color:'#e05c5c' }}>[filtered]</em> : m.text}</div>
                  {m.filtered && m.originalText && (
                    <div style={{ fontSize:11, color:'#aaa', marginTop:2 }}>Original: {m.originalText}</div>
                  )}
                </div>
              ))
            }
          </div>
        </Modal>
      )}
    </div>
  );
};

// ── CHAT BACKLOG ─────────────────────────────────────────────────────────────
const BUCKET_COLORS = { green: '#22c55e', yellow: '#eab308', orange: '#f97316', red: '#ef4444' };

const ChatBacklog = () => {
  const [backlog, setBacklog] = useState([]);
  const [summary, setSummary] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [bulkReason, setBulkReason] = useState('');
  const [bulkMsg, setBulkMsg] = useState(null);
  const [tab, setTab] = useState('backlog');

  const fetchBacklog = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api('/admin/chat/backlog');
      setBacklog(data.backlog || data.orders || []);
    } catch { setBacklog([]); }
    setLoading(false);
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const data = await api('/admin/chat/daily-summary');
      setSummary(data.summary || data);
    } catch { setSummary(null); }
  }, []);

  const fetchAgents = useCallback(async () => {
    try {
      const data = await api('/admin/chat/agents');
      setAgents(data.agents || []);
    } catch { setAgents([]); }
  }, []);

  useEffect(() => { fetchBacklog(); fetchSummary(); fetchAgents(); }, [fetchBacklog, fetchSummary, fetchAgents]);

  useEffect(() => {
    const iv = setInterval(() => { fetchBacklog(); fetchSummary(); }, 30000);
    return () => clearInterval(iv);
  }, [fetchBacklog, fetchSummary]);

  useEffect(() => {
    if (tab !== 'agents') return;
    const iv = setInterval(fetchAgents, 15000);
    return () => clearInterval(iv);
  }, [tab, fetchAgents]);

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleAll = () => {
    if (selected.size === backlog.length) setSelected(new Set());
    else setSelected(new Set(backlog.map(o => o._id)));
  };

  const handleBulkRefund = async () => {
    if (selected.size === 0) return;
    if (bulkReason.trim().length < 5) { setBulkMsg('Reason must be at least 5 characters'); return; }
    setBulkMsg(null);
    try {
      const data = await api('/admin/chat/bulk-refund', {
        method: 'POST',
        body: JSON.stringify({ chatOrderIds: [...selected], reason: bulkReason.trim() }),
      });
      setBulkMsg(`Refunded: ${data.refunded || 0}, Failed: ${data.failed || 0}`);
      setSelected(new Set());
      setBulkReason('');
      fetchBacklog();
      fetchSummary();
    } catch { setBulkMsg('Network error'); }
  };

  const [refundingId, setRefundingId] = useState(null);
  const handleSingleRefund = async (chatOrderId) => {
    const reason = window.prompt('Refund reason (min 5 chars):');
    if (!reason || reason.trim().length < 5) { setBulkMsg('Reason must be at least 5 characters'); return; }
    setRefundingId(chatOrderId);
    try {
      const data = await api(`/admin/chat/orders/${chatOrderId}/refund`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason.trim() }),
      });
      if (data.success) { setBulkMsg(`Refunded ₹${data.amount || 0}`); fetchBacklog(); }
      else setBulkMsg(data.error || 'Refund failed');
    } catch { setBulkMsg('Network error'); }
    setRefundingId(null);
  };

  const handleSetOffline = async (agentId) => {
    try {
      await api(`/admin/chat/agents/${agentId}/offline`, { method: 'PATCH' });
      fetchAgents();
    } catch {}
  };

  const ageBucket = (order) => order.ageBucket || 'green';

  const fmtAge = (mins) => {
    if (mins < 60) return `${mins}m`;
    if (mins < 1440) return `${Math.floor(mins / 60)}h ${mins % 60}m`;
    return `${Math.floor(mins / 1440)}d ${Math.floor((mins % 1440) / 60)}h`;
  };

  const tabStyle = (t) => ({
    padding: '8px 18px', cursor: 'pointer', border: 'none', borderRadius: '6px 6px 0 0',
    background: tab === t ? 'var(--card-bg, #23272f)' : 'transparent',
    color: tab === t ? '#fff' : '#999', fontWeight: tab === t ? 600 : 400, fontSize: 14,
  });

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 0, borderBottom: '1px solid #333' }}>
        <button style={tabStyle('backlog')} onClick={() => setTab('backlog')}>Backlog</button>
        <button style={tabStyle('agents')} onClick={() => setTab('agents')}>Agents Online</button>
        <button style={tabStyle('summary')} onClick={() => setTab('summary')}>Daily Summary</button>
      </div>

      {tab === 'summary' && summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, padding: '16px 0' }}>
          {[
            ['Total Orders', summary.totalOrders ?? 0],
            ['Claimed', summary.claimedOrders ?? 0],
            ['Auto-Refunds', summary.autoRefunds ?? 0],
            ['Median Claim', summary.medianClaimTimeMin != null ? `${summary.medianClaimTimeMin}m` : '—'],
            ['Claimed <15m', summary.pctClaimedUnder15Min != null ? `${summary.pctClaimedUnder15Min}%` : '—'],
            ['Claimed >1h', summary.claimedOver1hCount ?? 0],
          ].map(([label, val]) => (
            <div key={label} style={{ background: 'var(--card-bg, #23272f)', borderRadius: 8, padding: '14px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{val ?? '—'}</div>
            </div>
          ))}
        </div>
      )}

      {tab === 'agents' && (
        <div style={{ padding: '16px 0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #333', textAlign: 'left' }}>
                <th style={{ padding: '8px 6px' }}>Agent</th>
                <th style={{ padding: '8px 6px' }}>Status</th>
                <th style={{ padding: '8px 6px' }}>Active Orders</th>
                <th style={{ padding: '8px 6px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {agents.map(a => (
                <tr key={a._id} style={{ borderBottom: '1px solid #222' }}>
                  <td style={{ padding: '8px 6px' }}>{a.displayName || a.username}</td>
                  <td style={{ padding: '8px 6px' }}>
                    <span style={{
                      display: 'inline-block', width: 8, height: 8, borderRadius: '50%', marginRight: 6,
                      background: a.online ? '#22c55e' : '#666',
                    }} />
                    {a.online ? 'Online' : 'Offline'}
                    {a.suspended && <span style={{ color: '#ef4444', marginLeft: 6 }}>(Suspended)</span>}
                  </td>
                  <td style={{ padding: '8px 6px' }}>{a.activeCount ?? '—'}</td>
                  <td style={{ padding: '8px 6px' }}>
                    {a.online && !a.suspended && (
                      <button onClick={() => handleSetOffline(a._id)}
                        style={{ padding: '4px 10px', fontSize: 12, background: '#ef4444', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                        Set Offline
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {agents.length === 0 && <tr><td colSpan={4} style={{ padding: 16, textAlign: 'center', color: '#666' }}>No agents found</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'backlog' && (
        <div style={{ padding: '16px 0' }}>
          {backlog.length > 0 && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, color: '#999' }}>{selected.size} selected</span>
              <input value={bulkReason} onChange={e => setBulkReason(e.target.value)}
                placeholder="Refund reason (min 5 chars)" style={{ flex: 1, minWidth: 180, padding: '6px 10px', borderRadius: 4, border: '1px solid #444', background: '#1a1d24', color: '#fff', fontSize: 13 }} />
              <button onClick={handleBulkRefund} disabled={selected.size === 0}
                style={{ padding: '6px 14px', fontSize: 13, background: selected.size > 0 ? '#ef4444' : '#555', color: '#fff', border: 'none', borderRadius: 4, cursor: selected.size > 0 ? 'pointer' : 'default' }}>
                Bulk Refund ({selected.size})
              </button>
            </div>
          )}
          {bulkMsg && <div style={{ padding: '6px 10px', marginBottom: 8, borderRadius: 4, background: '#333', color: bulkMsg.includes('error') ? '#ef4444' : '#22c55e', fontSize: 13 }}>{bulkMsg}</div>}

          {loading ? <div style={{ textAlign: 'center', padding: 32, color: '#666' }}>Loading…</div> : backlog.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: '#22c55e', fontSize: 16 }}>✓ No unclaimed orders — backlog clear</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #333', textAlign: 'left' }}>
                  <th style={{ padding: '8px 6px' }}><input type="checkbox" checked={selected.size === backlog.length && backlog.length > 0} onChange={toggleAll} /></th>
                  <th style={{ padding: '8px 6px' }}>Age</th>
                  <th style={{ padding: '8px 6px' }}>Order</th>
                  <th style={{ padding: '8px 6px' }}>Reason</th>
                  <th style={{ padding: '8px 6px' }}>Game</th>
                  <th style={{ padding: '8px 6px' }}>Buyer</th>
                  <th style={{ padding: '8px 6px' }}>Agent</th>
                  <th style={{ padding: '8px 6px' }}>Amount</th>
                  <th style={{ padding: '8px 6px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {backlog.map(o => (
                  <tr key={o._id} style={{ borderBottom: '1px solid #222' }}>
                    <td style={{ padding: '8px 6px' }}><input type="checkbox" checked={selected.has(o._id)} onChange={() => toggleSelect(o._id)} /></td>
                    <td style={{ padding: '8px 6px' }}>
                      <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: BUCKET_COLORS[o.ageBucket || ageBucket(o)], marginRight: 6 }} />
                      {fmtAge(o.ageMinutes || o.waitingMinutes || 0)}
                    </td>
                    <td style={{ padding: '8px 6px', fontFamily: 'monospace', fontSize: 11 }}>{o.shortOrderId || '—'}</td>
                    <td style={{ padding: '8px 6px' }}>{o.label ? <span style={{ color: '#f97316', fontWeight: 600 }}>{o.label}</span> : <span style={{ color: '#999' }}>{o.status}</span>}</td>
                    <td style={{ padding: '8px 6px' }}>{o.gameName || '—'}</td>
                    <td style={{ padding: '8px 6px' }}>{o.buyerName || o.buyerEmail || '—'}</td>
                    <td style={{ padding: '8px 6px' }}>{o.agentName || <span style={{ color: '#666' }}>—</span>}</td>
                    <td style={{ padding: '8px 6px' }}>₹{fmt(o.amount)}</td>
                    <td style={{ padding: '8px 6px', display: 'flex', gap: 4 }}>
                      <button onClick={() => handleSingleRefund(o._id)} disabled={refundingId === o._id}
                        style={{ padding: '3px 8px', fontSize: 11, background: '#ef4444', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        {refundingId === o._id ? '…' : '💸 Refund'}
                      </button>
                      {o.chatOrderId && (
                        <a href={`${API_BASE.replace('/api/v1','')}/admin/chat/${o._id}`} target="_blank" rel="noreferrer"
                          style={{ padding: '3px 8px', fontSize: 11, background: '#333', color: '#5ba3f5', border: '1px solid #444', borderRadius: 4, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                          💬 Chat
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div style={{ display: 'flex', gap: 12, marginTop: 12, fontSize: 12, color: '#999', flexWrap: 'wrap' }}>
            <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: BUCKET_COLORS.green, marginRight: 4 }} />&lt;15m</span>
            <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: BUCKET_COLORS.yellow, marginRight: 4 }} />15m–1h</span>
            <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: BUCKET_COLORS.orange, marginRight: 4 }} />1h–6h</span>
            <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: BUCKET_COLORS.red, marginRight: 4 }} />&gt;6h</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ── NAV ───────────────────────────────────────────────────────────────────────
const NAV = [
  { group: 'Main',       items: [{ id: 'dashboard',    label: 'Dashboard',        icon: '⊞' }] },
  { group: 'Catalog',    items: [{ id: 'games',         label: 'Games & Packs',    icon: '🎮' }] },
  { group: 'Operations', items: [
    { id: 'orders',       label: 'Orders',           icon: '🛒' },
    { id: 'transactions', label: 'Transactions',     icon: '↔'  },
    { id: 'ledger',       label: 'Ledger',           icon: '📖' },
    { id: 'wallet',       label: 'Wallet Actions',   icon: '👝' },
    { id: 'kyc',          label: 'KYC Reviews',      icon: '🪪' },
    { id: 'chatOrders',   label: 'Chat Orders',      icon: '💬' },
    { id: 'chatBacklog',  label: 'Chat Backlog',     icon: '📊' },
  ]},
  { group: 'Users', items: [
    { id: 'users',   label: 'Users',   icon: '👥' },
    { id: 'coupons', label: 'Coupons', icon: '🏷️' },
  ]},
  { group: 'Settings',  items: [
    { id: 'gateways',   label: 'Payment Gateways',  icon: '💳' },
    { id: 'sms',        label: 'SMS Provider',      icon: '📱' },
    { id: 'blacklist',  label: 'Blacklisted Users', icon: '🚫' },
    { id: 'staffAccounts', label: 'Staff Accounts', icon: '🔑' },
  ]},
  { group: 'Analytics', items: [{ id: 'reports',  label: 'Reports',          icon: '📋' }] },
];

const SECTIONS = {
  dashboard: Dashboard, games: Games, orders: Orders,
  transactions: Transactions, ledger: Ledger, wallet: WalletActions,
  users: Users, coupons: Coupons, reports: Reports, gateways: Gateways, sms: SmsProvider,
  blacklist: Blacklist, kyc: KycReviews, staffAccounts: StaffAccounts, chatOrders: ChatOrders,
  chatBacklog: ChatBacklog,
};

// ── APP ROOT ──────────────────────────────────────────────────────────────────
function App() {
  const [isLoggedIn, setIsLoggedIn]   = useState(!!localStorage.getItem('admin_token'));
  const [active, setActive]           = useState('dashboard');
  const [adminInfo, setAdminInfo]     = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [theme, setTheme]             = useState(() => localStorage.getItem('admin_theme') || 'dark');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('admin_theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  useEffect(() => {
    if (isLoggedIn) api('/admin/me').then(data => {
      const info = data?.data || data?.admin || data;
      setAdminInfo(info);
      if (info?.isStaff) { setActive('kyc'); localStorage.setItem('admin_isStaff', '1'); }
      else { localStorage.removeItem('admin_isStaff'); }
    });
  }, [isLoggedIn]);

  const logout = () => { localStorage.removeItem('admin_token'); localStorage.removeItem('admin_isStaff'); setIsLoggedIn(false); };

  if (!isLoggedIn) return <Login onLogin={() => setIsLoggedIn(true)} />;

  const isStaff = adminInfo?.isStaff === true;
  const filteredNav = isStaff
    ? [{ group: 'Operations', items: [{ id: 'kyc', label: 'KYC Reviews', icon: '🪪' }] }]
    : NAV;

  const effectiveActive = isStaff && active !== 'kyc' ? 'kyc' : active;
  const ActiveSection = SECTIONS[effectiveActive] || SECTIONS['dashboard'];
  const activeLabel   = filteredNav.flatMap(g => g.items).find(i => i.id === effectiveActive)?.label || '';

  return (
    <div className="app">
      {sidebarOpen && <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-logo">
          <span className="logo-icon">⚡</span>
          <span>BloxFruitHub</span>
        </div>
        <nav className="sidebar-nav">
          {filteredNav.map(group => (
            <div className="nav-group" key={group.group}>
              <div className="nav-group-label">{group.group}</div>
              {group.items.map(item => (
                <button key={item.id} className={`nav-item ${effectiveActive===item.id ? 'active' : ''}`}
                  onClick={() => { setActive(item.id); setSidebarOpen(false); }}>
                  <span className="nav-icon">{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="admin-info">
            <div className="mini-avatar">{(adminInfo?.username || adminInfo?.name || adminInfo?.email || 'A')[0].toUpperCase()}</div>
            <div className="admin-name">{adminInfo?.username || adminInfo?.name || adminInfo?.email || 'Admin'}</div>
          </div>
          <button className="logout-btn" onClick={logout}>🚪 Logout</button>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <button className="hamburger" onClick={() => setSidebarOpen(o => !o)}>☰</button>
          <h1 className="page-title">{activeLabel}</h1>
          <div className="topbar-right">
            <button className="theme-toggle" onClick={toggleTheme} title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
            <div className="avatar">{(adminInfo?.name || 'A')[0].toUpperCase()}</div>
          </div>
        </header>
        <main className="content">
          <ActiveSection />
        </main>
      </div>
    </div>
  );
}

export default App;
